/**
 * The 4-step request form — React rebuild matching the production monolith UI
 * (legacy/smart-email-request/index.html). Left: the form card (in-card stepper
 * → 4 pages → nav footer). Right: a sticky live-preview rail.
 *
 *   1. Creative Request — division, initiative, focus, communities/market,
 *                         project, request type, nurture template
 *   2. Customize Eblast — subject/preheader (+ AI draft), message, deploy, Studio
 *   3. Audience         — the 7 Send Scope fields (the Seg3 builder is not
 *                         rendered since the MVP-only cutover; the seg state
 *                         still backs the submit's audience snapshot at its
 *                         recommended defaults + Step-1 sync, exactly like the
 *                         monolith's hidden seg3 IIFE in MVP mode)
 *   4. Disclaimer & Details — review + submit (POST /sfmc-submit)
 *
 * Validation contract (monolith parity, index.html L4760–4777 + btnSubmit):
 * navigation is FREE — Next never blocks. Submit validates all four pages,
 * paints .fmiss on each empty required wrapper (CSS appends " — required"),
 * jumps to the first bad page, marks touched-incomplete steps red, and writes
 * the reason into the Page-N-of-4 note. Editing a flagged field clears it.
 *
 * MVP is THE product (services/mvpMode.ts — MVP-only cutover 2026-08-09, no
 * ?mvp=0 / full mode): Send Scope IS step 3, the gallery is the 5 core
 * templates, and duplicate-a-job only lists jobs built from those 5.
 */
import { useCallback, useEffect, useRef, useState, type SyntheticEvent, useMemo} from "react";
import "./request-builder.css";
import type { FormState } from "../services/submit";
import { requiredBlanks, urlProblem } from "../services/echRequired";
import { suggestProjectName } from "../services/projectName";
import { dataGateBlanks } from "../services/echDataGate";
import { buildSubmitBody, utmQuery, disclaimerId, testStamp } from "../services/submit";
import { hostDataImages, swapInStr } from "../services/hostImages";
import { cropCardPhotos, pinCardImg } from "../services/cropCardPhotos";
import { telLinksPlusOne } from "../services/telLinks";
import type { AnyBlockInstance } from "../email/document";
import { realTemplateHtml } from "../services/realTemplates";
import SendScope from "../components/SendScope";
import Combo, { type ComboGroup } from "../components/Combo";
import type { SegState } from "../services/segData";
import { segToAudienceSnapshot } from "../services/segData";
import { draftCopy, type CopyContext } from "../services/aiCopy";
import { MVP_TPL_DEFAULT, mvpTemplateChoices } from "../services/mvpMode";
import {
  getTemplates, getTemplateHtml, getInitiatives, getSentEmail, submitRequest, mirrorRevise, searchCommunities,
  getMirrorJobs, type GalleryTemplate,
  getSubmittedHtml,
} from "../services/api";
/* /refdata comes through the page-level promise cache, not `./api` directly: the
   preview rail's `useEchDyn` asks the SAME route for the SAME division (record ids
   + addresses out of the same payload this pulls picklists from), so without the
   shared cache a division change fired it twice. Division refdata does not change
   mid-session, so a cached answer is also the right answer. See echFeeds.ts. */
import { feedRefdata as getRefdata } from "../services/echFeeds";
import DupModal from "../components/DupModal";
import { type DupJob, jstr, dupPrefetch, dupPickTemplate, deriveMessage, messageCode, tplStageOf, dupInvalidate } from "../services/dupJobs";
import { duplicateFromHtml, type Notice } from "../services/manifest";
import { sendTimeListing } from "../services/echSendTime";
import { sendTimeView } from "../services/echSendTimeView";
import { detect as dsDetect, assemble as dsAssemble, selected as dsSelected, followUps as dsFollowUps, sqftAnswer as dsSqftAnswer, eventFoodAnswer as dsFoodAnswer, SQFT_TYPES, applyDisclaimer, DISCLAIMER_ITEMS, ITEM as DS_ITEM, type Answers as DsAnswers } from "../services/disclaimer";
import { disclaimerCopy } from "../services/disclaimerLive";
import { resolveCommunityName, communityFill, cmCore } from "../services/communityFill";
import MvpTemplateGallery from "../components/MvpTemplateGallery";
import { echPreviewHtmlFor, echPreviewOf, echPreviewForOf, useEchTemplateList } from "../services/useEchTemplates";
import { isEch } from "../services/echRegistry";
import { echTplFor, echCanonicalName } from "../services/echTemplates";
import { useEchShell } from "../services/useEchShell";
import { echSeedStrip, type DynBlock } from "../services/echDyn";
import { echDivisionReset } from "../services/echDivisionReset";
import { amenFolderVersion } from "../services/echAmen";
import { useBynderMatchVersion } from "../services/echBynderMatch";
import { useAmenityIcons } from "../services/useAmenityIcons";
import { useEchDyn } from "../services/useEchDyn";
import { echLevel, echScopeReady } from "../services/echDyn";
import { PAST_DATE_MSG, dropPastDate, isPastDate, isPastDateTime, todayISO } from "../services/dateGuard";
import {
  divisionOptions, AOIS, MARKETS, MESSAGES, messageText, messageHuman, messageIsOffer, REQ_FOCUS, REQ_FOCUS_DEFAULT, REQ_TYPE_DEFAULT, RESUBMIT_REQ_TYPE,
  reqTypeRule, SPECIALTY, SPECIALTY_DEFAULT, TIP,
} from "../services/requestData";
import { divInits, divMarkets, divComms, MASTERPLANS, noClosed, activeInits, initiativeEnded } from "../services/snapshots";
import { buildNaming } from "../services/naming";
import { divisionQueueUrl } from "../services/requestData";
import { ui } from "../services/uiState";
import { dupJobEnabled, disclaimerBuilderEnabled } from "../services/features";
import { useLicenseLine, resolveLicenseLine } from "../services/useLicenseLine";
import { useLegalDisclaimers, resolveLegalOptions } from "../services/useLegalDisclaimers";
import { useEchDocFill } from "../services/useEchDocFill";
import type { NamedDraft } from "../services/namedDrafts";
/* one stable empty document — a fresh `[]` default per render would re-run every
   blocks-keyed effect on every keystroke */
const NO_BLOCKS: AnyBlockInstance[] = [];
const noSet = () => undefined;
const noPatch = () => undefined;
const noMake = (type: string): AnyBlockInstance => ({ id: "", type, data: {} } as unknown as AnyBlockInstance);

interface Props {
  form: FormState;
  /** functional updates supported (App passes React's setState directly) —
   *  stale-spread writes were losing sibling patches (2026-08-09 submit race) */
  setForm: (f: FormState | ((prev: FormState) => FormState)) => void;
  seg: SegState;
  setSeg: (s: SegState) => void;
  builtHtml: string;
  /** wizard page — owned by App so it survives the form↔studio view switch
   *  (monolith: the studio is an overlay, the form never loses its page) */
  step: number;
  setStep: (n: number) => void;
  onOpenStudio: () => void;
  /** The Studio's block doc, owned by App alongside `blocks` for StudioView.
   *
   *  Needed at SUBMIT time, not just in the Studio: the visual spec (Block Order, the named
   *  image fields, per-block content, Build Spec JSON) is flattened out of this doc into the
   *  Airtable row. Before 2026-08-20 the doc never reached the payload builder, so this app
   *  sent none of it and every image column in Airtable sat empty. */
  blocks?: AnyBlockInstance[];
  /** load real email HTML (a genuine sent email / a community fill) into the
   *  Creative Studio as the editable working doc — the monolith's
   *  __studio.loadHtml bridge (App wires it to the template editor). */
  onLoadStudioHtml?: (name: string, html: string) => void;
  /**
   * Load a duplicate back as REAL BLOCKS rather than as one flat document.
   *
   * Distinct from `onLoadStudioHtml` on purpose. That one hands the Studio an
   * opaque string, which is correct for an email this tool did not build — you
   * cannot honestly claim a field is governed when you only have rendered markup.
   * This one arrives with the original block list, so every cap, counter, lock and
   * dynamic fill applies to the copy exactly as it applied to the original.
   *
   * `notices` is what changed between then and now (a tightened cap, a field Layne
   * removed, markup that moved on, or the delivered HTML disagreeing with the
   * request). It is informational — the requester is never asked to choose.
   */
  onLoadStudioBlocks?: (name: string, blocks: AnyBlockInstance[], notices: Notice[]) => void;
  /**
   * Put App's DOCUMENT back to untouched — blocks, tplHtml/tplEdited, tplBespoke,
   * dupLoaded, dupNotices and the undo stacks (2026-08-28 division feedback batch).
   *
   * Called on exactly two events, both of which END the current document: starting
   * another request, and picking a DIFFERENT template. Not called while someone
   * works on a duplicate — per Jack, "duplicate is the starting point then should
   * be able to customize it", so a duplicate stays fully revisable until one of
   * those two events fires.
   */
  onResetDocument?: () => void;
  /** blank the form for a new request, keeping division + requested-by (App owns
   *  which fields survive) */
  onResetForm?: () => void;
  /** the request was filed (ok) — the saved draft is no longer needed (2026-09-08).
   *  Carries the SFMC asset id + submitted HTML so App can file proofing version 1. */
  onSubmitted?: (filed: { assetId: string; html: string }) => void;
  /** a NAMED draft picked in the template picker's Drafts mode (2026-09-18, dev/QA) —
   *  App loads its stored document; absent = the mode does not render */
  onOpenNamedDraft?: (d: NamedDraft) => void;
  /** the monolith's __studio.used() — auto-fill never clobbers a build the
   *  user made themselves */
  studioUsed?: boolean;
  /** App's document writers — the same three the Studio gets. With them the form
   *  runs the ONE fill pass (`useEchDocFill`) over a Studio-touched document while
   *  the Studio is closed, so a Step 1 pick change re-deals the cards the preview
   *  shows instead of freezing them until Design Email is reopened (2026-09-14) */
  setBlocks?: (u: (b: AnyBlockInstance[]) => AnyBlockInstance[]) => void;
  onPatch?: (id: string, patch: Record<string, unknown>, opts?: { history?: boolean }) => void;
  makeBlock?: (type: string) => AnyBlockInstance;
  /** the Studio already holds a template doc (App's tplHtml) — when false, the
   *  Open Creative Studio entry loads the SELECTED template first so the studio
   *  opens on the real template, never the block mock (monolith MVP behavior;
   *  Jack 2026-08-09 studio audit) */
  studioHasDoc?: boolean;
}

/* stepper sublabels (step 4 was the monolith's "Branding, references"; renamed 2026-10-07) */
const STEPS = [
  { t: "Creative Request", s: "Division, initiative" },
  { t: "Email Design", s: "Subject, content" },  /* renamed from "Customize Eblast" (Jack, 2026-09-10) */
  { t: "Audience", s: "Targeting, lists" },
  { t: "Disclaimer & Details", s: "Disclaimer, references" },   /* was "Branding & Details" — branding moved to Step 2 (Jack, 2026-10-07) */
];
/* production keeps the pre-#364 step 4 until the Disclaimer builder ships there
   (services/features.ts disclaimerBuilderEnabled, 2026-10-15) */
const STEPS_NO_DS = [...STEPS.slice(0, 3), { t: "Branding & Details", s: "Branding, references" }];

const TPL_DEFAULT = MVP_TPL_DEFAULT;
/* the same template on either side of the "ECH - " rename (2026-10-19) */
const sameTpl = (a: string, b: string): boolean => {
  const k = (s: string) => String(s || "").trim().toLowerCase().replace(/^ech\s*-\s*/, "").trim();
  return k(a) === k(b) || (!!echCanonicalName(a) && echCanonicalName(a) === echCanonicalName(b));
};
/**
 * SUBJECT AND PRE-HEADER CAPS (2026-08-24, Jack: "need to enforce the character count
 * but subject line can be up to 80").
 *
 * `SUBJ_COACH` is the number the counter goes amber at — the length that actually reads
 * well in an inbox. `SUBJ_HARD` is the ceiling the input refuses to exceed. They are two
 * different jobs and conflating them is what produced "88 / 50 chars" on a live request:
 * the old counter only coached, so nothing stopped a division submitting a subject that
 * cannot be built. Pre-header has one number — 100 is both the guidance and the cap.
 */
export const SUBJ_COACH = 50;
export const SUBJ_HARD = 80;
export const PRE_HARD = 100;

/** info "i" tooltip bubble (monolith .info) */
const Info = ({ tip }: { tip: string }) => <span className="info" data-tip={tip}>i</span>;

/* the engine's last-written values — module scope so a Studio round-trip (which unmounts
   FormView) cannot make an auto-generated name read as the requester's (2026-09-11) */
const PN_AUTO = { current: "" };
const SUBJ_AUTO = { current: "" };
const PRE_AUTO = { current: "" };

export default function FormView({ form, setForm, seg, setSeg, step, setStep, builtHtml, onOpenStudio, onLoadStudioHtml, onLoadStudioBlocks, onResetDocument, onResetForm, onSubmitted, onOpenNamedDraft, studioUsed = false, studioHasDoc = false, blocks = NO_BLOCKS, setBlocks, onPatch, makeBlock }: Props) {
  /* the Step 4 Disclaimer builder (#364) is dev / QA only — read once per mount (features.ts) */
  const dsOn = useMemo(() => disclaimerBuilderEnabled(), []);
  const dsApply = (html: string, paras: string[]): string => (dsOn ? applyDisclaimer(html, paras) : html);
  const [templates, setTemplates] = useState<GalleryTemplate[]>([]);
  const [initiatives, setInitiatives] = useState<string[]>([]);
  /* non-empty when the live initiative feed failed and the bundled snapshot is showing */
  const [initStale, setInitStale] = useState("");
  const [marketOpts, setMarketOpts] = useState<{ code: string; name: string }[]>(MARKETS.map((m) => ({ code: m.c, name: m.n })));
  /* division-scoped picklists from /refdata (monolith liveRefData) */
  const [refComms, setRefComms] = useState<string[]>([]);
  /* the division's Airtable record id, for the Done page's queue link (2026-09-10) */
  const [refDivId, setRefDivId] = useState<string>("");
  const [refMps, setRefMps] = useState<string[]>([]);
  const [refAois, setRefAois] = useState<string[]>([]);
  /* ── THE PICKERS SAY WHEN THEIR LIVE LIST IS STILL ON ITS WAY (2026-09-11) ─────
     Uche, testing call: "some sort of loading indicator while the preview or a
     dropdown is fetching." The bundled snapshot fills every picker at once and the
     live /refdata and /initiatives answers overlay it a beat later — so the field
     looked finished while it was still quietly wrong. Two flags, one per feed,
     drive Combo's `loading` (spinner in the face + a loading row in the panel).
     Mock approved by Jack: SPINNER-MOCK-20260911.html. */
  const [refLoading, setRefLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  /* monolith __subjTouched/__preTouched: once the USER types in subject/preheader they are
     never auto-overwritten. Programmatic writes (seed defaults, AI apply) don't count. */
  const subjTouched = useRef(false);
  const preTouched = useRef(false);
  /* the monolith's hard-coded Step-2 seed, retired 2026-09-10 — recognised so a draft
     saved with it is re-drafted from the brief instead of shipping webinar copy */
  const LEGACY_SEED_SUBJECT = "You could put 0% down on your first Lennar home";
  const LEGACY_SEED_PREHEADER = "See what a monthly payment looks like — watch the on-demand session in 30 minutes";
  /* THE LAST VALUE THE ENGINE WROTE (2026-09-08, pre-UAT e2e A3). `touched` only knows
     about typing, and it lives in THIS component, which unmounts while the Studio is
     open — so every Back onto Step 2 re-ran the draft over whatever was there (the
     engine picks at random: the pre-header changed on 3 of 3 Studio passes with no
     edit), and a duplicated job's real subject was replaced before anyone saw it. The
     engine may now only overwrite a field that is EMPTY or still holds ITS OWN last
     draft; anything else — typed, duplicated, restored across a remount — is the
     requester's and stays. */
  /* ── …AND THE STAMP SURVIVES THE STUDIO (2026-09-11, driven live on dev) ──────────
     Picked Brightlyn → opened Design Email → left → picked Granary Park and a new
     template: the Project Name stayed "Brightlyn at SilverLeaf …" for good. The
     stamps were `useRef`s in THIS component, which unmounts while the Studio is open;
     on remount they came back "", so the engine's own last name read as one the
     requester typed and was never touched again — Katie's "the project name should
     refresh on template change" (#229) held only until the first Studio visit. The
     stamps now live at module scope (the `ISC_CACHE` precedent in useEchDyn): a remount
     no longer forgets what the engine wrote, and `startOver` still clears them. A
     typed or duplicated value is still protected — it never equals the stamp. */
  const subjAuto = SUBJ_AUTO;
  const preAuto = PRE_AUTO;
  const [tplOpen, setTplOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [submitState, setSubmitState] = useState<"idle" | "busy" | "done" | "error">("idle");
  /* ── WHY IT FAILED, IN ONE LINE (2026-09-22) ─────────────────────────────────────
     The gateway's status and message (`403 · User is not authorized…`) or the thrown
     error, for the failed-submit screen and the Feedback context. Plain, short, and the
     one thing Farhan needs to find the request in the gateway log. */
  const [submitMsg, setSubmitMsg] = useState("");
  /* ── ONE CLICK IS ONE REQUEST (2026-08-28) ────────────────────────────────────
     Katie McCarthy, on today's standup: intermittent DUPLICATE rows in the intake
     table. The Submit button is disabled on `submitState === "busy"`, and that is a
     RENDER away — React batches, so two clicks inside the same frame both run
     `submit()` and both POST. A ref flips synchronously, in the same tick, which is
     the only thing that can close that window.
     The attempt id (`body.requestId`) is the other half: one id per attempt, sent in
     the payload — and since 2026-09-08 NOT shown on the Done screen (Katy: divisions
     cannot use it and it reads as a sandbox reference) — so a duplicate row is DIAGNOSABLE — two rows with the same id is
     this app posting twice, two rows with different ids is a requester submitting
     twice, and one id with a retry behind it is the worker. Without it, all three look
     identical in her table and nobody can tell which fix is the right one. */
  const sending = useRef(false);
  /* The two progress chips (monolith setPM: pm1 "Filed in Airtable" / pm2 "Template
     started") were RETIRED 2026-08-27. They were the whole status story and neither
     ever moved after the submit resolved, so a division that came back to the tab saw
     a frozen receipt. The four step cards now come from `doneStages`, which reads this
     browser's verdict AND the live mirror Job Status. */
  /* the naming engine's email name, frozen at submit */
  const [doneName, setDoneName] = useState("");
  /* ── THE AFTER-SUBMIT SCREEN'S OWN STATE (2026-08-27) ─────────────────────────
     `doneHtml` is the creative that ACTUALLY LEFT — `outDoc.html`, the same
     document the submit body carried and the same one the preview rail was
     showing, captured at submit. It is deliberately NOT `builtHtml`: that is
     App's raw render of the block state, without the dynamic fill or the card
     expansion the rail applies, so the old Done screen could show a different
     email from the one Creative received (the 2026-08-25 fault, same class).
     It is also not `outHtml`, which has the send-time %%[ ]%% listing lookup
     spliced in — that renders as stray text in a browser.
     `doneRec` / `doneStored` / `doneFiled` are the submit's verdict; `doneStage`
     is the live Job Status polled off the Projects (Mirror) row. */
  const [doneHtml, setDoneHtml] = useState("");
  const [doneRec, setDoneRec] = useState("");
  /* the submit verdict is still recorded (setters below) so a status surface can read it
     again; nothing on the Done page renders it since the step rail left (2026-09-10) */
  const [, setDoneStored] = useState(false);
  const [, setDoneFiled] = useState<"pending" | "filed" | "partial" | "error">("pending");
  /**
   * ── WHICH WRITE FAILED, ON THE SCREEN (2026-08-31, measured on dev) ──────────────
   * "Partly filed · Intake incomplete" told a requester something was wrong and gave
   * them no way to learn what. The worker already answers `problems[]` naming the
   * destination that refused (legacy row / mirror row / intake webhook) and this view
   * already had it — it went to `console.warn` and nowhere else, so two submits three
   * minutes apart, one partial and one clean, could not be told apart without a
   * developer reading the console.
   * Only the DESTINATION is surfaced, never `detail`: detail can carry an Airtable
   * error body, and a requester-facing screen is the wrong place for that.
   */
  const [, setDoneProblems] = useState<string[]>([]);
  const [, setDoneStage] = useState("");
  /* the review pane holds the WHOLE email, so its height is the document's, measured
     on load — an assumed height crops the footer off every send (Jack, 2026-08-27:
     "on the email review need to show the full thing") */
  const [donePrevH, setDonePrevH] = useState(0);
  const donePrevRef = useRef<HTMLIFrameElement | null>(null);
  const [fullOpen, setFullOpen] = useState(false);

  /* ── validation state (monolith: touched set + .fmiss wrappers + pgNote) ── */
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [touchedSteps, setTouchedSteps] = useState<Set<number>>(new Set());
  const [noteMsg, setNoteMsg] = useState("");
  const bodyRef = useRef<HTMLDivElement>(null);

  /* ── bottom-center toast (monolith __fvToast / toast()) ── */
  const [toastMsg, setToastMsg] = useState("");
  const [toastOn, setToastOn] = useState(false);
  const toastT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notify = (m: string) => {
    setToastMsg(m); setToastOn(true);
    if (toastT.current) clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToastOn(false), 2400);
  };

  /* a duplicated job's REAL delivered HTML (or its community fill) owns the
     preview until the user picks a template again (monolith S.__dupHtml) */
  const [dupHtml, setDupHtml] = useState("");
  /* the community auto-fill's output (the monolith parks it in the Studio as
     externalTplHtml; here it also drives the preview rail) */
  const [fillHtml, setFillHtml] = useState("");
  /* monolith __suppressAutoFill — auto-fill stands down while a dup settles */
  const suppressAutoFill = useRef(0);
  /* monolith __lastDupJob — the applied dup feeds the AI copy classifier blob */
  const lastDupJob = useRef<DupJob | null>(null);
  /* monolith __commContent.fromPrice — set by every successful communityFill */
  const commFromPrice = useRef(0);
  /* a dup-applied initiative survives the snapshot refill until the live list answers */
  const keepInitRef = useRef("");

  /* form writes clear any flag on the keys they touch (monolith: input/change → remove .fmiss) */
  const F = (patch: Partial<FormState>) => {
    if (missing.size) {
      const hit = Object.keys(patch).filter((k) => missing.has(k));
      if (hit.length) {
        const m = new Set(missing);
        hit.forEach((k) => m.delete(k));
        setMissing(m);
      }
    }
    setForm((prev) => ({ ...prev, ...patch })); /* functional — never drops sibling writes */
  };
  /* SendScope receives a setForm-shaped writer that still clears flags */
  const setFormClearing = (f: FormState | ((prev: FormState) => FormState)) => {
    setForm((prev) => {
      const next = typeof f === "function" ? f(prev) : f;
      if (missing.size) {
        const m = new Set(missing);
        let changed = false;
        m.forEach((k) => {
          const kk = k as keyof FormState;
          if (String((next[kk] ?? "")).trim() !== String((prev[kk] ?? "")).trim() && String(next[kk] ?? "").trim() !== "") { m.delete(k); changed = true; }
        });
        if (changed) setMissing(m);
      }
      return next;
    });
  };

  /* async callbacks (live-list overlays) must read the LATEST form, not the render
     that scheduled them — a ref mirror gives them that (monolith just read the DOM). */
  const formRef = useRef(form);
  formRef.current = form;
  const Fr = (patch: Partial<FormState>) => {
    if (missing.size) {
      const hit = Object.keys(patch).filter((k) => missing.has(k));
      if (hit.length) {
        const m = new Set(missing);
        hit.forEach((k) => m.delete(k));
        setMissing(m);
      }
    }
    setForm((prev) => ({ ...prev, ...patch })); /* functional — the ref spread could still lose same-commit sibling writes */
  };

  /* seed production defaults once — including the monolith's seeded subject/preheader.
     INTENTIONAL DIVERGENCE from the monolith (Jack 2026-08-19: "whole form starts
     blank"): the ATL division + ATL market seeds are GONE. Step 1 opens with no
     division and no market chip, so nothing downstream — the naming/UTM preview,
     the Studio's ECH dynamic fill, the intake payload — pre-fills with Atlanta.
     Everything below is a NON-division default and stays. */
  useEffect(() => {
    const patch: Partial<FormState> = {};
    if (!form.requestType) patch.requestType = REQ_TYPE_DEFAULT;
    if (!form.reqFocus) patch.reqFocus = REQ_FOCUS_DEFAULT;
    if (!form.template) patch.template = TPL_DEFAULT;
    if (!form.specialtyBranding) patch.specialtyBranding = SPECIALTY_DEFAULT;
    /* PRIMARY MESSAGE IS NOT PRE-SELECTED (2026-08-22, Jack: the Audience step should
       "default to blank"). The monolith pre-selected CS = Coming Soon, which is a guess
       about the send's purpose that nothing on Step 1 supports — a Sustain template is
       not a Coming Soon message. The combo already renders a "— select —" placeholder,
       and Primary Message is a required field, so a blank is caught at Done rather than
       shipping the wrong UTM. */
    /* ── NO HARD-CODED SUBJECT (2026-09-10, Jack: "there was an issue where this subject
       line and pre header had issue… I want this to adapt to page one and template").
       The monolith seeded the on-demand-webinar pair here ("You could put 0% down…"), and
       because a non-empty field is never overwritten by the Step-2 draft, EVERY new request
       opened Step 2 with webinar copy no matter the template or community. The pair now
       starts empty and the Step-2 engine writes it from the brief. */
    if (Object.keys(patch).length) F(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /* full catalog, no slice — the monolith's GALLERY holds every row (416 live);
       the old slice(0,300) cut "Launch - Sales Just Launched" out of the MVP
       family lookup (found 2026-08-08 during the MVP gallery port) */
    getTemplates().then((t) => setTemplates(t.templates || t.items || [])).catch(() => {}).finally(() => setTplsSettled(true));
  }, []);

  /* THE TEMPLATE LIST IS SFMC'S (2026-08-19 — Jack: "only pull in the sfmc
     blocks/template under the template tab"). Started HERE rather than inside the
     picker so the 40–60s cold pull is already running while the user fills Step 1;
     `sfmcBlocksOnce` guarantees the Studio's own read shares this one request. */
  const echTpls = useEchTemplateList();
  /* ── THE FORM HOLDS THE LIBRARY'S OWN NAME (Layne's "ECH - " rename, 2026-10-19) ──────
     A saved draft or the default can carry the other side of the rename ("Coming Soon" vs
     "ECH - Coming Soon"). Once the library loads, swap in the name it actually uses, so the
     picker, the Studio and Airtable's Email Nurture Template field all read the current
     name. Exact matches only (echCanonicalName); anything else is left alone. */
  useEffect(() => {
    const cur = Array.isArray(form.template) ? form.template[0] : form.template;
    if (!cur || Array.isArray(form.template)) return;
    const canon = echCanonicalName(cur);
    if (canon && canon !== cur) setForm((f) => ({ ...f, template: canon }));
  }, [echTpls.version, form.template]); // eslint-disable-line react-hooks/exhaustive-deps

  /* initiatives + markets + communities are division-scoped. Order matters
     (monolith fillInitiative/liveRefData): (1) the STATIC per-division snapshot
     renders INSTANTLY — the pickers are never empty, even offline; (2) the live
     worker lists overlay on success, KEEPING the user's pick when still present
     (else falling to the first item — a stale value never survives). */
  useEffect(() => {
    const c = form.divisionCode;
    if (!c) { setInitiatives([]); return; }
    /* (1) instant snapshots — DIV_INITS / DIV_MARKETS / DIV_COMMS + MASTERPLANS */
    const snap = divInits(c);
    const cur0 = form.initiative || "";
    /* ── A PICK THE SNAPSHOT DOESN'T KNOW IS STILL THE PICK (2026-09-08, pre-UAT
       e2e: 13 of 13 test submits filed Initiative "1067 …" after the requester
       had chosen "3337 …"). This view UNMOUNTS while the Studio is open
       (App.tsx renders `view === "form" && <FormView>`), so this effect re-runs
       on every Back. The build-time snapshot for JAX is three rows and has no
       3337 — the live list does — so the old branch reset the pick to snap[0]
       and the live overlay then accepted 1067 as valid. Any current pick now
       rides along in the option list until the live list rules on it; only an
       EMPTY pick takes the snapshot default. A true division change still resets,
       because onDivisionChange writes snap[0] itself before this effect runs. */
    if (cur0 && snap.indexOf(cur0) < 0) {
      setInitiatives([...snap, cur0].slice(0, 300));
    } else {
      setInitiatives(snap.slice(0, 300));
      if (!cur0) Fr({ initiative: snap[0] || "" });
    }
    const dm0 = divMarkets(c);
    setMarketOpts(dm0.map(([code, name]) => ({ code, name })));
    /* Market is required and division-determined; a division that has one and a form
       that has none (new request kept the division, a dup without a market, a reload
       that restored the division) gets the first market chipped — same as a fresh pick
       (2026-09-08, pre-UAT e2e A7: "requester is blocked at Next until they add it"). */
    {
      const mk = formRef.current.market;
      const has = Array.isArray(mk) ? mk.length > 0 : !!mk;
      if (!has && dm0.length) Fr({ market: [dm0[0][0]] });
    }
    setRefComms(divComms(c));
    setRefMps(noClosed(MASTERPLANS));
    /* warm the dup-jobs cache at boot + on every division change (monolith 2026-07-22) */
    if (dupJobEnabled()) dupPrefetch(c); /* no point warming a cache for a hidden button */
    /* (2) live overlays */
    setInitLoading(true);
    getInitiatives(c).then((i) => {
      const live = activeInits(i.initiatives || []).slice(0, 300);
      if (!live.length) return;
      setInitiatives(live);
      const wasDup = keepInitRef.current;
      keepInitRef.current = "";
      const cur = formRef.current.initiative || "";
      /* ── A COPIED JOB'S INITIATIVE THAT IS NO LONGER ACTIVE GOES BLANK (2026-09-08) ──
         Jack, standup, on duplicating a job tied to an ended initiative: "it populates
         everything, but the initiative would be blank, so then they would have to
         select a new active one." Falling to `live[0]` here would silently file the
         copy under whatever happens to sort first — a wrong initiative that LOOKS
         chosen. Blank is honest and the required-field check makes them pick. */
      if (cur && cur === wasDup && live.indexOf(cur) < 0) Fr({ initiative: "" });
      else if (!cur || live.indexOf(cur) < 0) Fr({ initiative: live[0] || "" });
      setInitStale("");
    }).catch(() => {
      /* Was a silent catch. The fallback here is divInits — a snapshot bundled at BUILD time,
         so on a failed fetch the picker quietly shows whatever was current when the app was
         last released, which can be weeks old. That silence is what let the CloudPage version
         of this bug survive: a division picks from a stale list and nothing says so. */
      setInitStale("Showing a saved initiative list — couldn't reach the live feed. Reload, and tell eMarketing if it persists.");
    }).finally(() => setInitLoading(false));
    setRefDivId("");
    setRefLoading(true);
    getRefdata(c).then((d) => {
      const dd = d as { markets?: string[]; communities?: string[]; masterplans?: string[]; areas?: string[];
        divisionId?: string;
        ids?: { communities?: Record<string, string> };
        addr?: { communities?: Record<string, { aoi?: string }> } };
      if (dd.divisionId) setRefDivId(String(dd.divisionId));
      if (Array.isArray(dd.markets) && dd.markets.length) {
        setMarketOpts(dd.markets.map((s) => {
          const [code, ...rest] = String(s).split(" - ");
          return { code: code.trim(), name: rest.join(" - ").trim() || code.trim() };
        }));
      }
      /* sold-out / closed communities never belong in the pickers (monolith noClosed) */
      const nc = (a?: string[]) => noClosed(Array.isArray(a) ? a.map(String) : []);
      if (nc(dd.communities).length) setRefComms(nc(dd.communities));
      if (nc(dd.masterplans).length) setRefMps(nc(dd.masterplans));
      /* ── THE AOI LIST IS WHAT THE COMMUNITIES SAY, NOT A SEPARATE PICKLIST
         (2026-08-22) ────────────────────────────────────────────────────────────
         Jack picked "Duval" and the table stayed on placeholders. `/refdata`
         `areas` is its own Airtable picklist and carries names no community in the
         division is tagged with — JAX's 50 communities are Jacksonville, St.
         Augustine, Green Cove Springs, Palm Coast and St Johns; not one says Duval.
         An option that cannot fill anything is a dead control, so the list is built
         from the communities' OWN `aoi` values, with the picklist as the fallback
         for a division whose rows carry none. */
      /* THE BAG IS NATIONAL — WALK THE DIVISION'S OWN IDS (2026-08-22, second pass).
         `addr.communities` carries all 3,095 communities Lennar has; `ids.communities`
         is the 50 in THIS division. Deriving from the bag produced the ~600-name
         national list Jack was already staring at (that was my own first fix making
         it worse). Division ids → their rows → their AOI values is the scoped answer:
         JAX comes out as Green Cove Springs, Jacksonville, Palm Coast, St Johns and
         St. Augustine, which is exactly the set that can fill a table. */
      const fromComms: string[] = [];
      const seenAoi: Record<string, 1> = {};
      const commBag = (dd.addr && dd.addr.communities) || {};
      const divIds = (dd.ids && dd.ids.communities) || {};
      Object.keys(divIds).forEach((nm) => {
        const row = commBag[String(divIds[nm])] || {};
        String(row.aoi || "").split(",").forEach((raw) => {
          const a = raw.trim();
          if (!a || a === "-" || seenAoi[a.toLowerCase()]) return;
          seenAoi[a.toLowerCase()] = 1; fromComms.push(a);
        });
      });
      fromComms.sort((a, b) => a.localeCompare(b));
      setRefAois(fromComms.length ? fromComms : nc(dd.areas));
    }).catch(() => {}).finally(() => setRefLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.divisionCode]);

  /* division change — the monolith's divisionCode change handler (index.html L4325):
     dependents reset, the new division's FIRST market auto-chips, geo fields clear.
     The effect above then refills every picker (snapshot first, live overlay after). */
  const onDivisionChange = (v: string) => {
    if (!v || v === form.divisionCode) return;
    /* THE CARDS GO BACK TO THE FEED TOO (2026-09-28) — a switch kept the previous
       division's Featured cards and Listing rows, picks included, so a BOI → NSH switch
       shipped Boise floorplans in a Nashville email (see echDivisionReset.ts). Only a real
       switch: the first pick of a division has nothing stale, and no-op docs write no
       history (an empty undo step would flip `studioUsed`). */
    if (form.divisionCode && setBlocks) {
      const next = echDivisionReset(blocks as unknown as DynBlock[]);
      if (next !== (blocks as unknown as DynBlock[])) setBlocks(() => next as unknown as AnyBlockInstance[]);
    }
    const dm = divMarkets(v);
    const snap = divInits(v);
    F({
      divisionCode: v,
      market: dm.length ? [dm[0][0]] : [],
      community: "",
      masterplan: "",
      areaOfInterest: "",
      initiative: snap[0] || "",
    });
  };

  /* ── derived chip arrays (markets = form.market[], comms = form.community CSV) ── */
  const markets = Array.isArray(form.market) ? form.market : form.market ? [form.market] : [];
  const setMarkets = (arr: string[]) => F({ market: arr });
  const comms = form.community ? form.community.split(",").map((s) => s.trim()).filter(Boolean) : [];
  const setComms = (arr: string[]) => F({ community: arr.join(", ") });
  /* masterplan chips (monolith __commSel kind:"MP") — CSV in form.masterplan; the first
     one is what the intake's "Masterplan Name" column reads, like cmSync's first-of-kind */
  const mps = form.masterplan ? form.masterplan.split(",").map((s) => s.trim()).filter(Boolean) : [];
  const setMps = (arr: string[]) => F({ masterplan: arr.join(", ") });
  /* one typed chip list — MP chips first, then communities (monolith renderCm) */
  const cmSel: { kind: "MP" | "CM"; name: string }[] = [
    ...mps.map((name) => ({ kind: "MP" as const, name })),
    ...comms.map((name) => ({ kind: "CM" as const, name })),
  ];
  /* per-masterplan collection exclusion (monolith cmColsOpen/__commSel[i].exc,
     L4268–4309): each MP chip carries a ▾ that opens a live-SFMC checkbox panel;
     unchecked collections are excluded, the chip shows the kept "n/N" count. */
  const [mpCols, setMpCols] = useState<Record<string, { cols: { name: string; code: string }[] | null; exc: string[] }>>({});
  const [colsOpenFor, setColsOpenFor] = useState<string | null>(null);
  const cmColsClose = () => setColsOpenFor(null);
  function cmColsOpen(name: string) {
    if (colsOpenFor === name) { cmColsClose(); return; }
    setColsOpenFor(name);
    if (mpCols[name] && mpCols[name].cols) return; /* cached — reopen instantly */
    setMpCols((m) => ({ ...m, [name]: { cols: null, exc: (m[name] && m[name].exc) || [] } }));
    searchCommunities(cmCore(name) || name).then((d) => {
      const items = ((d && d.items) || []).map((c) => ({ name: c.name, code: c.code }));
      setMpCols((m) => {
        const exc = ((m[name] && m[name].exc) || []).filter((x) => items.some((c) => c.name === x));
        return { ...m, [name]: { cols: items, exc } };
      });
    }).catch(() => {
      setMpCols((m) => ({ ...m, [name]: { cols: [], exc: [] } }));
    });
  }
  const cmColToggle = (name: string, col: string, keep: boolean) =>
    setMpCols((m) => {
      const e = m[name] || { cols: null, exc: [] };
      const exc = keep ? e.exc.filter((x) => x !== col) : e.exc.indexOf(col) > -1 ? e.exc : [...e.exc, col];
      return { ...m, [name]: { ...e, exc } };
    });
  /* division change clears the chips — the panel and exclusions go with them
     (monolith L4325: window.__commSel=[]; cmColsClose()) */
  useEffect(() => { setColsOpenFor(null); setMpCols({}); }, [form.divisionCode]);
  const removeCm = (it: { kind: "MP" | "CM"; name: string }) => {
    if (colsOpenFor != null) cmColsClose(); /* monolith: any chip removal closes the panel */
    return it.kind === "MP" ? setMps(mps.filter((x) => x !== it.name)) : setComms(comms.filter((x) => x !== it.name));
  };
  const addCm = (v: string) => {
    const kind = v.slice(0, 2), name = v.slice(3);
    if (!name) return;
    if (kind === "MP" && !mps.includes(name)) setMps([...mps, name]);
    if (kind === "CM" && !comms.includes(name)) setComms([...comms, name]);
  };

  const reqFocus = form.reqFocus || REQ_FOCUS_DEFAULT;
  const reqType = form.requestType || REQ_TYPE_DEFAULT;
  const showTpl = reqTypeRule(reqType).tpl;
  const tplName = (Array.isArray(form.template) ? form.template[0] : form.template) || TPL_DEFAULT;

  const [depDate, setDepDate] = useState(() => (String(form.targetDeploy || "").match(/^(\d{4}-\d{2}-\d{2})/) || [])[1] || "");
  const [depTime, setDepTime] = useState(() => (String(form.targetDeploy || "").match(/(\d{1,2}:\d{2})$/) || [])[1] || "10:00");
  const writeDeploy = (d: string, t: string) => F({ targetDeploy: d ? `${d} ${t}` : "" });
  /* ── THE FORM OWNS THE DEPLOY MOMENT (2026-08-28) ─────────────────────────────
     `depDate`/`depTime` are a parsed VIEW of `form.targetDeploy`, not a second copy
     of it. They used to be seeded once and then cleared by hand in `startOver`,
     which is a divergence waiting to happen: the completeness gate reads the LOCAL
     pair while the payload reads the FORM, so the moment the two disagree Submit
     becomes a SILENT NO-OP — enabled, clickable, posts nothing, says nothing. Main's
     own duplicate-submit test caught it the moment startOver began clearing fields.
     One value, set and cleared in one place. */
  useEffect(() => {
    const raw = String(form.targetDeploy || "");
    const d = (raw.match(/^(\d{4}-\d{2}-\d{2})/) || [])[1] || "";
    const t = (raw.match(/(\d{1,2}:\d{2})$/) || [])[1] || "10:00";
    setDepDate((p) => (p === d ? p : d));
    setDepTime((p) => (p === t ? p : t));
  }, [form.targetDeploy]);

  /* ── validation: the monolith's REQS (L4760), keyed so .fmiss hits exact wrappers ── */
  const reqPairs = (n: number): Array<[key: string, val: string]> => {
    if (n === 1) {
      /* in PAGE order (restored 2026-09-15), so "jump to the first missing field" lands on
         the topmost blank: Division, Due Date, Initiative, Focus, Market, Project Name, Template */
      const a: Array<[string, string]> = [
        ["divisionCode", form.divisionCode || ""],
        ["targetDueDate", form.targetDueDate || ""],
        ["initiative", form.initiative || ""],
        ["reqFocus", reqFocus],
        ["market", markets.length ? "x" : ""],
        ["projectName", form.projectName || ""],
        /* requestType field removed from the UI per Jack 2026-08-08 — the state
           keeps its default so reqTypeRule() still gates the template picker */
      ];
      if (showTpl) a.push(["template", tplName]);
      return a;
    }
    if (n === 2) return [
      ["subject", form.subject || ""], ["preheader", form.preheader || ""],
      ["deployDate", depDate],
      /* moved from page 4 with the field itself (2026-08-28) — a required field has to
         be validated on the page it lives on, or the step-jump lands on the wrong one */
      ["specialtyBranding", form.specialtyBranding || SPECIALTY_DEFAULT],
      /* primaryMessage moved to page 3 with the field itself (Katy 2026-08-19) —
         it has to travel with the input or the step-jump lands on the wrong page */
    ];
    if (n === 3) {
      /* monolith REQS[3] — sfInclude is required in BOTH modes (autoSF keeps it
         filled while the plain fields are hidden in full mode) */
      return [
        ["primaryMessage", form.primaryMessage || ""],
        ["senderType", form.senderType || ""],
        ["utmTarget", form.utmTarget || ""],
        ["emailAudienceType", form.emailAudienceType || ""],
        ["sfListsInclude", form.sfListsInclude || ""],
      ];
    }
    /* page 4: the Disclaimer follow-ups, only when the form would ask them (2026-10-02) —
       Event Food on a Grand Opening, both Accolade answers when the copy makes a claim */
    if (n === 4) {
      if (!dsOn) return [];   /* production: no Disclaimer follow-ups (features.ts) */
      const a: Array<[string, string]> = [];
      if (dsAsk.eventFood) a.push(["dsEventFood", dsFoodAnswer(dsDet, dsAns)]);
      if (dsAsk.accolades) a.push(["dsAccoladeType", dsAns.accoladeType || ""], ["dsAccoladeOrg", (dsAns.accoladeOrg || "").trim()]);
      if (dsAsk.sqft) a.push(["dsSqftType", dsSqftAnswer(dsDet, dsAns)]);
      return a;
    }
    return [];
  };
  const pageComplete = (n: number) => reqPairs(n).every(([, v]) => String(v || "").trim() !== "");
  const fmiss = (key: string) => (missing.has(key) ? " fmiss" : "");

  const goStep = (n: number) => {
    if (n !== step) setTouchedSteps((t) => new Set(t).add(step));
    setStep(n);
    ui.step = n; /* the feedback context reads the wizard step (monolith fbContext) */
    setNoteMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ── STEP 1 DRIVES THE PREVIEW RAIL (2026-08-20 — Jack: "i'd also like this
     preview if possible to update dynamic info when someone is filling out the form
     such as division code and community logo header updates and etc.") ───────────
     The SAME hook and the SAME ctx the Studio canvas fills from (StudioView L536),
     so the rail and the canvas can never disagree about one template. Assembled
     from Step 1 alone — the fields App already hands StudioView, in the same shape.
     `reqFocus` rather than `form.reqFocus`: the form treats an unset focus as its
     default ("Market"), and the rail has to read the level the same way the form's
     own validation does. (`echLevel` maps "" to market too, so this is the same
     answer stated explicitly rather than a behaviour change.)
     Both views mount this hook, but never at once — App renders one of them — and
     every route it asks for now goes through the page-level cache in echFeeds.ts,
     so a swap costs nothing. */
  const echCtx = useEchDyn({
    focus: reqFocus,
    divisionCode: form.divisionCode || "",
    community: form.community,
    masterplan: form.masterplan,
    areaOfInterest: form.areaOfInterest,
    market: form.market,
    initiative: form.initiative,
  });
  /* THE FORM RUNS THE FILL PASS TOO (2026-09-14). The two views swap, so a document
     the Studio had produced froze the moment the requester came back to Step 1:
     remove a community and the hero followed (the rail computes that itself) but the
     Featured cards kept the REMOVED community's plan until Design Email was reopened.
     Driven on dev with Jie's picks (Cypress Preserve + Brunswick Plantation → drop
     Cypress): "Owen" — a Cypress plan — stayed on the rail beside Brunswick's. Same
     pass as the Studio's, gated exactly like the preview's use of `blocks`: only a
     document the Studio has touched, and only when App handed over the writers. */
  /* the fill's writes stay out of undo (#131) — see App.patchData */
  const onPatchFill = useCallback((id: string, p: Record<string, unknown>) => { if (onPatch) onPatch(id, p, { history: false }); }, [onPatch]);
  useEchDocFill({
    blocks, echCtx,
    setBlocks: setBlocks || noSet, onPatch: onPatch ? onPatchFill : noPatch, makeBlock: makeBlock || noMake,
    enabled: studioUsed && blocks.length > 0 && !!setBlocks && !!onPatch && !!makeBlock,
  });
  /* what the fill pill names: the one community or masterplan the send is about,
     else the market, else the division (2026-09-11) */
  const fillName = (() => {
    if (echCtx.picks.length === 1 && echCtx.picks[0].name) return echCtx.picks[0].name;
    const mk = Array.isArray(form.market) && form.market.length === 1 ? String(form.market[0]) : "";
    /* the chip holds the CODE ("JAX"); the name lives on marketOpts — measured live
       2026-09-11, the pill read "Filling in JAX…" */
    const named = mk ? ((marketOpts.find((m) => m.code === mk.split(" - ")[0].trim()) || {}).name || mk.split(" - ").slice(1).join(" - ")) : "";
    return named || form.divisionCode || "your content";
  })();
  /* read by the effect below, whose deps must NOT include the ctx — a ctx change
     must re-fill the rail without re-running /template-html (see the second effect) */
  const echCtxRef = useRef(echCtx);
  echCtxRef.current = echCtx;
  /* true while the ASSEMBLED template owns the rail. A ref and not state: it is set
     inside the effect that also writes `previewHtml`, so it must not itself
     re-trigger that effect. The refill effect below reads it to stay out of the way
     of a real /template-html answer, which still wins (monolith fallback order). */
  const asmOwns = useRef(false);

  /* ── preview rail: render the selected template live (/template-html), zoomable ── */
  const [previewHtml, setPreviewHtml] = useState("");
  /* no WRONG email while loading (Jack 2026-08-09 "why am i still seeing this" —
     the embedded Altamira sample flashed on boot until /templates.json + the real
     /template-html landed): the embedded fallback only renders once the gallery
     list has SETTLED with no match, or a real fetch actually failed. While in
     flight the rail shows a loading card — the monolith's showPvLoading pattern
     ("Loading <template>…", index.html L4650), which never flashes a different
     email. */
  const [tplsSettled, setTplsSettled] = useState(false);
  const [tplLoading, setTplLoading] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(0.4);
  const [frameH, setFrameH] = useState(1400);
  const realwrapRef = useRef<HTMLDivElement>(null);
  const colRef = useRef<HTMLDivElement>(null);   /* left form column (monolith ".s1grid > div") */
  const cardRef = useRef<HTMLDivElement>(null);  /* .formcard */
  const zoomRef = useRef<HTMLDivElement>(null);  /* .fc-zoom — the per-step content-fit wrapper */
  const userZoom = useRef<number | null>(null);  /* monolith `zoom` — null = auto-fit */
  const lastZ = useRef(0.4);                     /* monolith lastZ */
  const frameHRef = useRef(frameH);
  frameHRef.current = frameH;
  useEffect(() => {
    /* The hard-coded family lookup (`mvpFamilyTemplate`) that used to back-fill this
       went with the five hard-coded templates. A live SFMC template's canonical name
       ("Coming Soon") rarely matches a /templates.json catalog row exactly, so the
       replacement is the ASSEMBLED template itself — see `embedded()` below. Same
       source as the picker card and the Studio canvas, so all three agree. */
    const t = templates.find((x) => x.name === tplName);
    /* monolith renderPreview (index.html L4638): the embedded REAL templates keep the
       rail on the full real email (hero webinar graphic, house photo, SmartBuy module,
       footer) whenever /template-html fails or returns empty — including fully offline,
       where the gallery list itself never loads. %%UTM%% gets the live utmQuery() string,
       like fillReal. */
    const embedded = () => {
      /* THE LIVE TEMPLATE, ASSEMBLED. This is what the picker card shows and what
         the Studio is about to build, so the rail must not show something else.
         It also gates `openStudio()`, which refuses to open with an empty rail —
         without this a live template that has no catalog twin would be unpickable. */
      /* …and with STEP 1'S ANSWERS IN IT (2026-08-20). Same fill the Studio runs.
         With nothing answered yet `echPreviewHtmlFor` returns the template's own
         defaults, so this is the previous behaviour plus a filled state, never a
         blanked one. `echCtxRef` and not `echCtx` — see the refill effect below. */
      const asm = echPreviewHtmlFor(tplName, echCtxRef.current);
      if (asm) { asmOwns.current = true; setPreviewHtml(asm); return; }
      asmOwns.current = false;
      const f = formRef.current;
      const nm = buildNaming({
        divisionCode: f.divisionCode, market: Array.isArray(f.market) ? f.market[0] : f.market,
        community: String(f.community || "").split(",")[0].trim() || undefined,
        messageCode: f.primaryMessage, initiative: f.initiative,
        deployDate: depDate || undefined, audType: f.emailAudienceType, utmTarget: f.utmTarget,
      });
      const q = utmQuery(nm.utm);
      /* ⛔ NEVER SHOW A BAKED-IN PRE-ECH EMAIL FOR AN SFMC TEMPLATE (2026-08-24, per Jack:
         *"we should only be using the sfmc templates"*). `realTemplateHtml` holds 2024-era
         Stensul copies keyed on the old gallery names; the loose `/coming soon/i` test below
         matched the LIVE name "Coming Soon" too, so a cold load — blocks still in flight, or
         /sfmc-blocks down — painted that old email into the rail and it reads as the product.
         If the name resolves to a live SFMC template, the rail stays on its loading/empty card
         and re-renders when the blocks land (this effect re-runs on `echTpls.version`). */
      if (echTplFor(tplName)) { setPreviewHtml(""); return; }
      /* ── EXACT NAMES ONLY (2026-08-24, from an audit) ─────────────────────────────
         There used to be a `/coming soon/i` FAMILY fallback here, for the days when the
         gallery's "Tease - Coming Soon" had to reach the embedded "Fast Track - Tease
         Coming Soon". It now matches the LIVE SFMC name "Coming Soon" — which is the
         app's default — so during the cold window (`ECH_MAP` is built from /sfmc-blocks
         and is empty for 16–60s, or forever if that route is down) the guard above
         cannot fire and this line painted a 2024 Stensul email as the selected
         template. An exact key match is the only safe form: these embedded emails are
         keyed on legacy gallery names, and a live SFMC template must never resolve to
         one of them. With no exact match the rail keeps its loading/empty card. */
      setPreviewHtml(realTemplateHtml(tplName, q));
    };
    if (t) {
      setTplLoading(true);
      getTemplateHtml(t.id).then((h) => {
        /* AN ANSWERED STEP 1 OUTRANKS THE CATALOG SNAPSHOT (2026-08-20). /template-html
           is a stored file — it cannot show the division's phone or the picked
           community, so once the assembled render has taken the rail (see the refill
           effect below) letting this land would undo the fill. Measured in Chrome:
           "Tease - Coming Soon" DOES match a /templates.json row, so on this template
           — the default — the catalog answer won every time and Jack's own path never
           reached the fill at all. */
        if (asmOwns.current) { embedded(); return; }
        if (h && h.length > 300) { asmOwns.current = false; setPreviewHtml(h); } else embedded();
      }).catch(embedded).finally(() => setTplLoading(false));
    }
    else if (tplsSettled) embedded();
    /* else: gallery list still in flight — the rail stays on the loading card,
       never a different email (the flash Jack caught) */
    /* `echTpls.version` is in the deps because the assembly above only becomes
       possible once the block library lands — without it the rail stays on the
       loading card for the whole session on a cold pull. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplName, templates, tplsSettled, echTpls.version]);

  /* ── THE RAIL RE-FILLS AS STEP 1 IS ANSWERED (2026-08-20) ─────────────────────
     A SECOND effect, and not `echCtx` bolted onto the deps of the one above, for
     one reason: that effect owns the /template-html FETCH. Adding the ctx to its
     deps would re-issue the request (and flick `tplLoading`) on every division,
     market and community change — a network round trip to redraw something that is
     computed locally. This one re-renders the assembled preview and touches nothing
     else.
     `asmOwns` gates it so the monolith's fallback order still holds: a real
     /template-html answer, or an embedded/thumbnail fallback, owns the rail and is
     not overwritten by an assembly it already lost to.
     `echCtx` is a useMemo inside `useEchDyn` keyed on Step-1 primitives and the
     feed results, so this fires when an ANSWER changes — not on every keystroke of
     the project name, and not on every render. */
  /* THE FORM HAS TO PRIME THE ICON LIBRARY ITSELF (2026-08-21).
     `useAmenityIcons` lived only in StudioView, so /amenity-icons was never fetched
     until the requester OPENED the Studio — meaning the preview rail, the surface
     they look at on Steps 1 and 2, always resolved amenities against the 4-icon
     fallback. That is why JAX Granary Park's nine showed as three. The hook shares
     one in-flight promise per page, so calling it here AND in the Studio still
     fetches exactly once. */
  useAmenityIcons();
  const amenLibV = amenFolderVersion();
  /* the rail's card photos take the same Bynder crops as the Studio (2026-09-25) */
  const bynV = useBynderMatchVersion();
  useEffect(() => {
    /* ANSWERING STEP 1 IS WHAT HANDS THE RAIL OVER. Until then the monolith's order
       stands untouched: /template-html, else the embedded real email, else the
       thumbnail. There is nothing to fill with yet, so switching sources early would
       change what Jack sees on load for no gain. `echScopeReady` is the same
       "has Step 1 actually been answered at this level" test the Inspector uses. */
    const scoped = echScopeReady(echLevel(echCtx.focus, (echCtx.picks || []).length), echCtx);
    if (!asmOwns.current && !scoped) return;
    const asm = echPreviewHtmlFor(tplName, echCtx);
    /* "" only when the library isn't loaded / the name isn't a live template — the
       rail keeps what it has rather than going blank */
    if (!asm) return;
    /* AND IT KEEPS IT AFTERWARDS, deliberately: clearing the community must fall back
       to the TEMPLATE'S OWN defaults (which is what `echPreviewHtmlFor` returns with
       no scope), not leave the old community's name sitting in the header. A template
       re-pick re-arms the whole decision through the effect above. */
    asmOwns.current = true;
    setPreviewHtml(asm);
    /* `amenLibV` IS AN INPUT, NOT NOISE (2026-08-21): the rail's amenity fill matches
       the community's Airtable amenities against the icon library, which arrives
       asynchronously, so without it the first (fallback) render is the one that
       sticks. Same defect the Studio had. */
  }, [echCtx, tplName, echTpls.version, amenLibV, bynV]);

  /* monolith renderPreview fallback order (index.html L4630-4683): the rail NEVER
     shows the block mock — real template HTML, else the gallery thumbnail, else a
     neutral card; the studio build owns the rail only once the studio was actually
     USED (__studio.used() gate, L4637). The old unconditional `|| builtHtml` showed
     the webinar-flavored STARTER blocks as if they were the selected template
     whenever /template-html couldn't answer (always over file://) — Jack 2026-08-09:
     "why do you show this default template when it's not available". */
  const tplThumb = templates.find((x) => x.name === tplName)?.thumb || "";
  const escT = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const previewFallback = tplThumb
    ? `<!doctype html><html><body style="margin:0;background:#fff"><img src="${tplThumb}" alt="${escT(tplName)}" style="width:600px;max-width:100%;display:block;margin:0 auto"></body></html>`
    : `<!doctype html><html><body style="margin:0;background:#fff;font-family:Arial,Helvetica,sans-serif"><div style="padding:70px 40px;text-align:center;color:#8a8578"><div style="font-size:15px;font-weight:800;color:#1B365D;margin-bottom:7px">${escT(tplName || "Pick a template")}</div><div style="font-size:13px;line-height:1.5">Preview will load when the template library syncs.<br>Open Design Email to start from this template.</div></div></body></html>`;
  /* ONE LOADING INDICATOR, NOT TWO (2026-09-12, Jack on QA: "why does it still show the
     old loading view on top of the new one … should only have the new one and should
     keep that view until the email is fully showing"). The card used to carry its own
     "Loading <template>…" line, and the pill (2026-09-11) then floated over it. The
     card is now a blank white surface; the pill is the only indicator and it reads
     "Loading <template>…" while the template is fetched, "Filling in …" while the
     feed fills, and stays up until the frame has painted the final email. */
  const loadingCard = `<!doctype html><html><body style="margin:0;background:#fff"></body></html>`;
  /* ONE guarded studio opener for EVERY entry point (Step-2 CTA + the rail's
     Creative Studio button — the second route Jack hit, 2026-08-09): load the
     selected template first; with no template doc available at all, don't open
     the block mock — say why. */
  const openStudio = () => {
    if (!studioHasDoc && previewHtml && onLoadStudioHtml) onLoadStudioHtml(tplName, previewHtml);
    /* A COLD BROWSER MUST NOT BE LOCKED OUT (2026-08-19). `previewHtml` is the
       template assembled from Layne's SFMC blocks, so it is empty until
       /sfmc-blocks answers — 16–60s on a first load. This refusal therefore made
       the Creative Studio button do NOTHING for the best part of a minute, and the
       only feedback was a toast telling Jack to wait. Measured live in Chrome on a
       cleared snapshot: `.es` never appeared at all.
       The Studio now has an honest loading state of its own for exactly this case
       (`echPending`), so with a template SELECTED it opens and says what it is
       doing. The refusal is kept only for the case it was written for: no template
       to open at all. */
    if (!studioHasDoc && !previewHtml && !tplName) {
      notify("Pick a template first — it opens in the Studio from there.");
      return;
    }
    onOpenStudio();
  };
  /* ── THE STUDIO'S OWN DOCUMENT OUTRANKS THE ASSEMBLED TEMPLATE (2026-08-24) ─────
     BOI, via Feedback: "Created email using creative studio … but when I close creative
     studio, the preview is blank." Not blank — it was the TEMPLATE. `previewHtml` (the
     freshly assembled blocks, still on Layne's placeholder art) came first in this
     chain, so a requester who had just placed a real Bynder photo and edited copy came
     back to the form and saw the untouched template, hero placeholder and all. The
     comment above this line already said the studio build owns the rail once the studio
     has been used; the order didn't. `studioUsed` is App's own "the studio produced
     something" gate, so nothing changes for a requester who never opened it. */
  /* ── THE RAIL IS THE STUDIO DOCUMENT WHENEVER THERE IS ONE (2026-08-24) ─────────
     Jack, after the first attempt: "preview still isn't showing the edits in creative
     studio, must always reflect." My first pass gated this on `studioUsed`, which is
     App's DIVERGENCE test — `blocks` differing from the STARTER mock. A requester who
     edits COPY or swaps a PHOTO changes field data, not the block list, so that test
     stayed false and the rail kept showing the assembled template. The document itself
     is the right condition: if there are blocks, they ARE the email, and `builtHtml` is
     the same renderer the Studio canvas uses. Before the Studio is ever opened the doc
     is the assembled template, so this shows the same thing it always did.

     CORRECTED SAME DAY, from a fresh-eyes audit of the diff: the condition WAS
     `blocks.length`, which is truthy from FIRST PAINT — App seeds `blocks` with the six
     STARTER mock blocks (logo/hero/headline/body/cta/footer) — so that version
     re-broke the exact bug recorded 50 lines above ("why do you show this default
     template when it's not available", 2026-08-09) and made `previewHtml`, the loading
     card and the thumbnail fallback unreachable. The test is therefore "is this an ECH
     DOCUMENT": STARTER blocks are legacy registry types and answer false, while the
     assembled template and every edited version of it answer true. Jack's requirement
     still holds — a copy edit or a photo swap changes field data, not the block list,
     and both show — without letting the mock impersonate a template. */
  /* ── ONE PREVIEW PATH, FED BY THE LIVE DOCUMENT (2026-08-24, corrected) ─────────
     `builtHtml` is App's raw `renderDocument(blocks)`: it carries the Studio's edits but
     NOT the card expansion, which lived in StudioView's effect and therefore only ran
     while the Studio was mounted. Driving both surfaces showed the split — canvas two
     plan cards, rail one. So the rail renders the SAME blocks through the SAME preview
     function the template path uses: fill passes, card expansion and render in one
     place. A copy is passed because React owns that array and the function mutates. */
  /* ── ONE DOCUMENT, SHOWN AND SENT (2026-08-25) ───────────────────────────────────
     Uche, on the live dev site: "the preview pane is not getting updated with my actual
     selections, so the email that gets created in SFMC looks incorrect." Those were two
     different reads of the same request — the rail assembled and FILLED a document,
     while the submit sent App's raw `renderDocument(blocks)` gated on a
     STARTER-divergence test that is false for anyone who never opened the Creative
     Studio. A form-only request therefore shipped SFMC the untouched template.
     `echPreviewOf` returns the filled doc with its html, so both consumers below read
     the same object. */
  /* ── THE SUBMIT HTML IS REBUILT WHEN THE SHELL ARRIVES (2026-09-16) ─────────────────
     Jie's QA submit at 18:33Z (SFMC asset 1034061) shipped the 4 KB fallback head while the
     same page's preview showed ECH_Blank_Template. `renderDocument` picks the shell up from
     `echShell()` at render time, and this memo re-ran only on blocks/ctx/templates — so a
     page that loaded on a snapshot with no blank template (any browser last here before
     09-15 20:52Z) built the document shell-less and kept it until something else changed.
     The shell is now a dependency: the moment the live pull adopts it, the rail, the
     Done gate and the payload rebuild on it. */
  const echShellNow = useEchShell();
  const liveDoc = useMemo(() => (
    blocks.some((b) => isEch(b.type))
      /* SEED-STRIPPED like the assembly path (measured 2026-08-24: without this the
         header read "Community Name | Jacksonville / St. Augustine, FL" — the seed AND
         the locator, the duplication this file fixed once already). App's `blocks` never
         went through `pvBlock`, so the authored placeholder is still in the data and the
         rail's blank-guard then refuses to clear it. */
      ? echPreviewOf(blocks.map((b) => {
        const c = { ...b, data: { ...b.data } };
        echSeedStrip(c as unknown as AnyBlockInstance);
        return c;
      }) as unknown as DynBlock[], echCtx)
      : { html: "", doc: [] as DynBlock[] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [blocks, echCtx, echTpls.version, echShellNow]);
  const liveDocHtml = liveDoc.html;
  /* ── THE DISCLAIMER BUILDS ITSELF FROM THE REQUEST (2026-10-02, services/disclaimer.ts) ──
     Template default (Digital Ops' "Default disclaimers") + whatever the email mentions,
     written into the ECH_Disclaimer footer of the preview AND the submitted email. The copy
     it reads is every string the requester controls: subject, preheader, block text. */
  /* each piece carries the field it sits in, so a chip's reason can say WHERE
     ("“schools” in Body Copy · Copy"), per the 2026-10-07 Disclaimer Artifact Review */
  const dsCopy = useMemo(() => {
    /* the doc the email is built from: the Studio's, else the template's own (echPreviewForOf —
       the same assembly the submit path files), so default copy and CTAs are read too.
       The reading itself is shared with Design Email (services/disclaimerLive.ts). */
    const src = (liveDoc.doc.length ? liveDoc.doc : blocks.length ? blocks
      : (echTplFor(tplName) ? echPreviewForOf(tplName, echCtx).doc : [])) as unknown as Array<{ type?: string; data?: Record<string, unknown> }>;
    return disclaimerCopy(src, form, liveDocHtml || (studioUsed ? builtHtml : "") || dupHtml || fillHtml || previewHtml);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveDoc.doc, blocks, form.subject, form.preheader, form.headline, tplName, echCtx, liveDocHtml, studioUsed, builtHtml, dupHtml, fillHtml, previewHtml]);
  const dsAns = useMemo<DsAnswers>(() => form.dsAnswers || {}, [form.dsAnswers]);
  const dsReqNo = disclaimerId(form.disclaimer || "");
  const dsDet = useMemo(() => dsDetect({ template: tplName, reqFocus, copy: dsCopy.pieces, amenities: dsCopy.amen }), [tplName, reqFocus, dsCopy]);
  const dsAsk = dsFollowUps(dsDet, dsAns);
  const dsPicked = dsSelected(dsDet, dsAns);
  /* the Seller's Broker / Construction License line for the picked division + communities' states (2026-10-23) */
  const dsLicense = useLicenseLine(form, dsOn);
  /* legal's own wording, read from Legal Reviews › Disclaimer Options (2026-10-28) */
  const dsLegal = useLegalDisclaimers(dsOn);
  const dsParas = useMemo(() => dsAssemble(dsDet, { ...dsAns, requestNo: dsReqNo }, { division: form.divisionCode, deploy: form.targetDeploy, license: dsLicense }),
    [dsDet, dsAns, dsReqNo, form.divisionCode, form.targetDeploy, dsLicense, dsLegal]); // eslint-disable-line react-hooks/exhaustive-deps
  /* ── THE GENERIC DISCLAIMER UNTIL STEP 4 (Jack, 2026-10-07) ──────────────────────────────
     Steps 1–3 show the template's generic default (Digital Ops' per-template disclaimer) with
     nothing added; the auto-built one takes over once the requester reaches Step 4, where the
     Disclaimer section that explains it lives. Submit always files the built one. */
  const [dsReached, setDsReached] = useState(step >= 4);
  useEffect(() => { if (step >= 4) setDsReached(true); }, [step]);
  const dsGeneric = useMemo(() => dsAssemble(dsDetect({ template: tplName, copy: [] }), {}, { division: form.divisionCode, deploy: form.targetDeploy, license: dsLicense }),
    [tplName, form.divisionCode, form.targetDeploy, dsLicense, dsLegal]); // eslint-disable-line react-hooks/exhaustive-deps
  const dsSet = (patch: Partial<NonNullable<FormState["dsAnswers"]>>) => {
    F({ dsAnswers: { ...(form.dsAnswers || {}), ...patch } });
    const k = Object.keys(patch)[0];
    const key = k === "eventFood" ? "dsEventFood" : k === "accoladeType" ? "dsAccoladeType" : k === "accoladeOrg" ? "dsAccoladeOrg" : k === "sqftType" ? "dsSqftType" : "";
    if (key && missing.has(key)) { const m = new Set(missing); m.delete(key); setMissing(m); }
  };
  /* the Disclaimer Builder request # — inside the builder's grid on dev / QA, on its own in
     Request Details where the builder is off (production) */
  const dsReqField = (
                        <div className="f">
                          <label>Disclaimer Builder <Info tip={TIP.disclaimer} /></label>
                          {/* ── THE TYPED VALUE MUST NOT VANISH SILENTLY (2026-08-31, Farhan) ────
                              Katy's column links to a numeric-keyed table, so submit files only a
                              clean request number (disclaimerId). Before this, a value like
                              "DB-533" or pasted text was dropped to "" AT SUBMIT with no feedback.
                              On blur: a recoverable value is normalised in place ("DB-533" → 533);
                              an unrecoverable one is LEFT VISIBLE with the warning below — the
                              requester keeps what they typed and learns it won't file as typed. */}
                          <input
                            value={form.disclaimer || ""}
                            placeholder="Disclaimer Builder request #"
                            onChange={(e) => F({ disclaimer: e.target.value })}
                            onBlur={() => {
                              const raw = String(form.disclaimer || "").trim();
                              const dn = disclaimerId(raw);
                              if (dn && dn !== raw) F({ disclaimer: dn });
                            }}
                          />
                          {String(form.disclaimer || "").trim() !== "" && !disclaimerId(form.disclaimer) && (
                            <div style={{ fontSize: 12, color: "#a33", marginTop: 4 }}>
                              Enter just the request number (e.g. 533) — this won't file as typed.
                            </div>
                          )}
                        </div>
  );
  /* ── AN EDIT OWNS THE PREVIEW (2026-08-28, ATL) ────────────────────────────────
     Reported as "if I make a change it's not saving that change. It reverts back to
     the original version of the duplicate job." The edit IS saved — `dupHtml` holds
     the original delivered HTML, is never rewritten by a Studio edit (its only
     writers are applyJob's two branches and the ★ favorite pick), and led this
     chain. So the rail painted the original over the edit on every render, and the
     edited document — `builtHtml`, i.e. `tplEdited || renderDocument(blocks)` — sat
     last and was never reached.

     `studioUsed` is the gate, so an UNTOUCHED duplicate still shows its original
     exactly as before; the swap happens only once someone has actually edited. This
     changes nothing about the eblast itself (per Jack) — same document, same
     markup, it is only which of the two the preview reads. */
  const editedHtml = studioUsed ? builtHtml : "";
  const tplBusy = !(liveDocHtml || editedHtml || dupHtml || fillHtml || previewHtml) && (tplLoading || !tplsSettled);
  /* a block that picks its content at send (AMPscript) shows its placeholder here, never
     its raw script — display only, the submit reads `liveDoc`/`outDoc`, not this string
     (services/echSendTimeView.ts, 2026-10-15); the disclaimer paragraphs go in first */
  const shownHtml = sendTimeView(dsApply(liveDocHtml || editedHtml || dupHtml || fillHtml || previewHtml
    || (tplBusy ? loadingCard : previewFallback), dsReached ? dsParas : dsGeneric), liveDocHtml ? liveDoc.doc : blocks);
  /* the pill holds until the FRAME has painted the settled email: `awaitPaint` is raised
     whenever loading or filling starts and dropped by the frame's onLoad once neither is
     in flight — so there is no gap between "filling" ending and the email showing */
  const [awaitPaint, setAwaitPaint] = useState(false);
  const busyNow = tplBusy || echCtx.pending;
  useEffect(() => {
    if (busyNow) { setAwaitPaint(true); return; }
    /* the frame's onLoad normally drops it; if the settled html is byte-identical the
       frame never reloads, so a short backstop drops it instead of leaving it up */
    const t = window.setTimeout(() => setAwaitPaint(false), 600);
    return () => window.clearTimeout(t);
  }, [busyNow]);
  const busyRef = useRef(busyNow); busyRef.current = busyNow;
  /* the wording follows the reason, and holds the LAST reason while waiting for the paint */
  const pillLast = useRef("");
  if (tplBusy) pillLast.current = `Loading ${tplName || "template"}…`;
  else if (echCtx.pending) pillLast.current = `Filling in ${fillName}…`;
  const pillText = pillLast.current || `Loading ${tplName || "template"}…`;
  const showPill = !!shownHtml && (busyNow || awaitPaint);

  /* ── FIT ENGINE — VIEWPORT LOCK (Jack 2026-08-08, overrides all prior sizing
     rules: "the whole app must fit the browser window with NO scrolling on any
     step" — topbar, both panes and the Back/Next navbar all visible at once,
     and NO internal fc-body scrolling either).
       · the SHELL is CSS-locked (request-builder.css "VIEWPORT LOCK" block):
         html/body/.rb can never scroll; .stage-wrap/.s1grid flex-consume the
         height under the topbar; card and rail stretch to the SAME viewport-
         derived height on every step — equal panes by construction, constant
         across steps (Jack's earlier "always the same size" holds for free)
       · PER-STEP CONTENT FIT: after render, if the step's content (.fc-zoom)
         overflows fc-body, CSS-zoom it down to clientHeight/scrollHeight — the
         monolith's fitPage body-zoom technique (its fixed .9 on steps 2–3),
         computed per step so EVERY step fits with zero scroll. ~.75 is the
         readability floor; zero-scroll is the HARD rule, so a taller step
         may go below it, never under .5. Steps that fit
         naturally run at zoom 1. Recomputed on step change / resize / content
         change (ResizeObserver below).
       · the PREVIEW fit recomputes inside the same CSS-fixed rail — the whole
         email visible at the fitted zoom, no rail scroll (user ± zoom still
         overrides; ⤢ returns to fit). The old .40 cap died with the pinned
         frame: the fit IS the frame now. */
  const applyZoom = () => {
    const rw = realwrapRef.current;
    if (!rw) return;
    const nat = frameHRef.current || 1600, nw = 600;
    const availH = rw.clientHeight || 400, availW = rw.clientWidth || 600;
    /* 12px of it is `.zoomsizer`'s top margin — the gap that makes it obvious the
       email is fitted rather than clipped (request-builder.css). It has to come out
       of the height the fit is allowed to use, or the gap would push the last row
       into a scroll. Keep the two numbers in step. */
    const TOP_GAP = 12;
    let fit = Math.min((availH - 14 - TOP_GAP) / nat, (availW - 12) / nw, 1);
    if (!isFinite(fit) || fit <= 0) fit = 0.3;
    fit = Math.max(fit, 0.12);
    const z = userZoom.current ?? fit;
    lastZ.current = z;
    setPreviewZoom(z);
  };
  /**
   * FITALL'S OWN WRITES MUST NOT RE-TRIGGER FITALL (2026-08-21, Jack: "it's like
   * adjusting in size forever or moving violently until a field is selected").
   *
   * `zoom` AFFECTS LAYOUT, so every write changes the very height the next pass
   * measures, and a ResizeObserver on the same element re-runs the engine 60ms
   * later. The old comment claimed the ±0.004 hysteresis made a steady-state pass a
   * no-op so the loop self-terminated — and it does converge on a still page. It
   * does NOT converge while something else is also changing height: opening a combo
   * panel, adding a community chip, a web font swapping in. Each of those lands the
   * engine mid-flight, and observer-fire → write → observer-fire chases itself for
   * as long as the content keeps moving. It stops on focusing a field because focus
   * changes no height, so nothing re-fires it — which is exactly the tell Jack
   * described.
   *
   * The guard is the standard break for an observe→write→observe cycle: mark the
   * window in which the engine is writing, and have the observer ignore fires that
   * arrive inside it. Released on the next frame (not synchronously) because
   * ResizeObserver delivers asynchronously, after the write that caused it.
   */
  const fitting = useRef(false);
  /* the content height fitAll LEFT BEHIND. The observer compares against this to
     recognise its own echo — see the note above and the observer below. */
  const lastFitH = useRef(-1);
  const fitAll = () => {
    /* synchronous re-entry only; the async echo is handled by the observer, NOT by
       holding this flag across frames.
       ⚠️ AN EARLIER VERSION RELEASED THIS FLAG IN requestAnimationFrame, AND THAT
       DEADLOCKED THE ENGINE. rAF is paused in a background tab, so the flag stayed
       true and fitAll never ran again — measured: a pane overflowing 618px of content
       into 400px, a dispatched resize, and ZERO zoom writes with data-fitzoom stuck at
       1.000. Never gate this on a frame callback. */
    if (fitting.current) return;
    fitting.current = true;
    try { fitAllInner(); } finally { fitting.current = false; }
    const zw = zoomRef.current;
    lastFitH.current = zw ? zw.offsetHeight : -1;
  };
  const fitAllInner = () => {
    const fb = bodyRef.current, zw = zoomRef.current;
    if (fb && zw) {
      if (window.innerWidth < 1040) {
        /* mobile keeps natural flow + page scroll (the CSS lock is ≥1040-only) */
        zw.style.removeProperty("zoom");
        zw.style.removeProperty("--fitz");
        delete zw.dataset.fitzoom;
      } else {
        const cs = window.getComputedStyle(fb);
        const padV = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
        let z = parseFloat(zw.style.getPropertyValue("zoom")) || 1;
        const write = (v: number) => {
          zw.style.setProperty("zoom", String(v));
          zw.style.setProperty("--fitz", String(v)); /* .custpanel's edge-bleed divides by this */
        };
        /* converge on the zoom that exactly fits (≤4 passes): offsetHeight is
           in the wrapper's LOCAL units, so each zoom write reflows the text at
           the wider local width — re-measure after applying. Starting from the
           CURRENT zoom (not 1) makes a steady-state call a measured no-op, so
           the ResizeObserver feedback loop self-terminates. */
        for (let i = 0; i < 4; i++) {
          const want = Math.max(0.5, Math.min(1, (fb.clientHeight - padV) / (zw.offsetHeight || 1)));
          if (Math.abs(want - z) < 0.004) break;
          z = want;
          write(z);
        }
        /* hard zero-scroll backstop, checked in physical px on the pane itself */
        if (fb.scrollHeight > fb.clientHeight + 1 && z > 0.5) {
          z = Math.max(0.5, z * (fb.clientHeight / fb.scrollHeight));
          write(z);
        }
        zw.dataset.fitzoom = z.toFixed(3);
      }
    }
    applyZoom();
  };
  const fitAllRef = useRef(fitAll);
  fitAllRef.current = fitAll;
  const applyZoomRef = useRef(applyZoom);
  applyZoomRef.current = applyZoom;

  /* Keep the preview's clip box exactly the size of the painted (scaled) email — see the
     comment on .zoomsizer below. transform doesn't affect layout, so the box has to be told;
     measuring the child is the only source that can't disagree with what the user sees. */
  const zoomSizerRef = useRef<HTMLDivElement | null>(null);
  const zoomInnerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const inner = zoomInnerRef.current, box = zoomSizerRef.current;
    if (!inner || !box || typeof ResizeObserver === "undefined") return;
    const sync = () => {
      const r = inner.getBoundingClientRect();
      if (r.height > 0) box.style.height = Math.ceil(r.height) + "px";
      if (r.width > 0) box.style.width = Math.ceil(r.width) + "px";
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(inner);
    /* a scale change repaints without resizing the child's border box, so watch the style too */
    const mo = new MutationObserver(sync);
    mo.observe(inner, { attributes: true, attributeFilter: ["style"] });
    const t = window.setTimeout(sync, 200);   /* after the .1s transform transition settles */
    return () => { ro.disconnect(); mo.disconnect(); window.clearTimeout(t); };
  }, [shownHtml, previewZoom, frameH]);
  /* window resize + the monolith's load-time double pass (L4742–4743) */
  useEffect(() => {
    const onR = () => fitAllRef.current();
    window.addEventListener("resize", onR);
    const t1 = window.setTimeout(onR, 150), t2 = window.setTimeout(onR, 600);
    /* The monolith refits after EVERY render() (fitSoon, L4741). In React the
       step content grows AFTER mount — async divisions/initiatives/templates
       fill the combos, conditional fields appear, web fonts swap — so watch the
       content wrapper itself: any height change re-runs fitAll (debounced like
       fitSoon). fitAll's own zoom writes DO re-fire this observer — `zoom` is a
       layout property — which is why `fitAll` now guards against re-entry rather
       than relying on its hysteresis to absorb the echo. See the note on `fitting`
       above; without the guard this pair thrashes for as long as anything else on
       the page is still changing height. */
    let roT = 0;
    const ro = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => {
          /* DROP OUR OWN ECHO. `zoom` is a layout property, so fitAll's write resizes
             this very element and fires this observer. Comparing against the height
             fitAll left behind distinguishes "the engine just moved it" from "the page
             genuinely changed" — no timer, no frame callback, nothing that can stall in
             a background tab and strand the engine. A real change has a different
             height, and gets the debounce it always had. */
          const zw = zoomRef.current;
          if (zw && zw.offsetHeight === lastFitH.current) return;
          window.clearTimeout(roT);
          roT = window.setTimeout(() => fitAllRef.current(), 60);
        })
      : null;
    if (ro && zoomRef.current) ro.observe(zoomRef.current);
    try { document.fonts?.ready?.then(() => fitAllRef.current()); } catch { /* older engines */ }
    return () => {
      window.removeEventListener("resize", onR);
      window.clearTimeout(t1); window.clearTimeout(t2);
      if (ro) ro.disconnect();
      window.clearTimeout(roT);
      /* defensive: earlier builds zoomed the body on steps 2–3 — make sure a
         hot-swapped session never keeps a stale zoom (constant-frame engine
         never sets one) */
      document.body.style.removeProperty("zoom");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* per-step recompute — monolith goPage: requestAnimationFrame(fitAll) + setTimeout(fitAll,130) */
  useEffect(() => {
    const raf = requestAnimationFrame(() => fitAllRef.current());
    const t = window.setTimeout(() => fitAllRef.current(), 130);
    return () => { cancelAnimationFrame(raf); window.clearTimeout(t); };
  }, [step, submitState]);
  /* the email's natural height changed (iframe load) → refit (monolith L5119).
     applyZoom() has to run too, not just fitAll() (2026-08-19, Jack: "what this block
     white space below the disclaimer?"). The preview rail sizes its clip box as
     `frameH * previewZoom` and scales the iframe by that same `previewZoom` — but only
     fitAll ran here, so `previewZoom` stayed fitted to the PREVIOUS email while the box
     was already sized for the NEW frameH. The two disagree by exactly the ratio of the
     two heights: a shorter email leaves dead white below the disclaimer, a taller one
     gets clipped. Refitting the zoom against the new height keeps the box and the scale
     derived from the same number. */
  useEffect(() => { fitAllRef.current(); applyZoomRef.current(); }, [frameH]);
  /**
   * THE PREVIEW FIT ONLY EVER GOT TALLER (2026-08-20 — Jack: "still there… should be
   * like 25% for longer ones and 35% for smaller, should be adaptive so you don't
   * have that long white space below it").
   *
   * `applyZoom` already fits adaptively: `min((availH - 14) / frameH, …)`. The bug was
   * `frameH`, which was read as `body.scrollHeight`. **Inside an iframe, scrollHeight
   * can never report less than the iframe's own height** — and the iframe is sized
   * `frameH × previewZoom` from the previous measurement. So the number could only
   * ratchet UP: measure a long template once, switch to a short one, and scrollHeight
   * keeps returning the tall viewport instead of the new content. The fit was then
   * computed against a frame taller than the email, which is exactly the white space
   * below the footer, and why the percentage never adapted per template.
   *
   * The fix is to measure the CONTENT, not the viewport: the furthest bottom edge of
   * the body's own children, which is independent of how tall the iframe happens to
   * be. `scrollHeight` stays as a fallback for a document with no element children.
   *
   * AND TO RE-MEASURE WHEN THE HEIGHT ACTUALLY SETTLES. An email's height is mostly
   * images, and `onLoad` fires before they finish decoding, so a load-time-only
   * measurement is a measurement of the wrong document. Every image gets a listener
   * and `fonts.ready` is awaited; each one re-measures, and because the value is now
   * content-derived it is free to go DOWN as well as up.
   */
  const measureFrame = (doc: Document | null | undefined): number => {
    if (!doc || !doc.body) return 1400;
    let bottom = 0;
    const kids = Array.prototype.slice.call(doc.body.children) as HTMLElement[];
    for (const el of kids) {
      const r = el.getBoundingClientRect();
      if (r.height > 0) bottom = Math.max(bottom, r.bottom);
    }
    /* body margin below the last child still shows as paper, so keep it */
    const bs = doc.defaultView?.getComputedStyle(doc.body);
    bottom += parseFloat(bs?.marginBottom || "0") || 0;
    /* a document with no element children (plain text) has no rects to measure */
    if (bottom <= 0) bottom = doc.body.scrollHeight || 0;
    return Math.max(Math.ceil(bottom), 400);
  };
  const onFrameLoad = (e: SyntheticEvent<HTMLIFrameElement>) => {
    const frame = e.currentTarget;
    /* the settled email has painted — the loading pill can go (see `awaitPaint`) */
    if (!busyRef.current) setAwaitPaint(false);
    const remeasure = () => {
      try { setFrameH(measureFrame(frame.contentDocument)); }
      catch { setFrameH(1400); }
    };
    remeasure();
    try {
      const doc = frame.contentDocument;
      if (!doc) return;
      const imgs = Array.prototype.slice.call(doc.images) as HTMLImageElement[];
      imgs.forEach((im) => {
        if (im.complete) return;
        im.addEventListener("load", remeasure, { once: true });
        im.addEventListener("error", remeasure, { once: true });
      });
      doc.fonts?.ready?.then(remeasure).catch(() => {});
      /* backstop for anything that changes height without an event we can hear */
      window.setTimeout(remeasure, 400);
    } catch { /* cross-origin can't happen on srcDoc, but never break the preview */ }
  };
  /* monolith zoom buttons (L5120–5122): ± steps 0.15 from the CURRENT z; ⤢ = back to auto-fit */
  const zOut = () => { userZoom.current = Math.max(0.1, (lastZ.current || 1) - 0.15); applyZoom(); };
  const zIn = () => { userZoom.current = Math.min(3, (lastZ.current || 1) + 0.15); applyZoom(); };
  const zFit = () => { userZoom.current = null; applyZoom(); };

  /* ── PROJECT NAME AUTO-SUGGEST (monolith pnSuggest/pnApply, L4331–4350) ──
     Builds "Geo + Promotion + Eblast for M/D", fills the field while it's empty
     or still holding our last suggestion, backs off permanently once the user
     types their own name. The rule itself lives in services/projectName.ts
     (2026-09-10): the community leads whenever one is picked, and the nurture
     TEMPLATE is the promotion — the initiative's words only when no template is. Katie (Form Feedback 2026-09-10): the geo falls
     back to the community the initiative names before the division, and the page-2 Target
     Deployment Date beats the page-1 Target Due Date. */
  const pnAuto = PN_AUTO; /* module-scope stamp — see SUBJ_AUTO */
  function pnSuggest(): string {
    const f = formRef.current;
    const opt = divisionOptions().find((d) => d.code === f.divisionCode);
    const picked = (Array.isArray(f.template) ? f.template[0] : f.template) || "";
    const mk = Array.isArray(f.market) ? f.market : f.market ? [f.market] : [];
    return suggestProjectName({
      focus: f.reqFocus || "",
      community: f.community || "",
      masterplan: f.masterplan || "",
      areaOfInterest: f.areaOfInterest || "",
      divisionLabel: opt ? opt.label : f.divisionCode || "",
      markets: mk.map((code) => ({ code, name: (marketOpts.find((m) => m.code === code) || {}).name })),
      initiative: f.initiative || "",
      template: showTpl && picked && !sameTpl(picked, TPL_DEFAULT) ? picked : "",
      targetDueDate: f.targetDueDate || "",
      targetDeploy: f.targetDeploy || "",
    });
  }
  useEffect(() => {
    const t = setTimeout(() => { /* monolith defers 80ms after the change */
      const raw = pnSuggest();
      if (!raw) return;
      const s = testStamp(raw);   /* "TEST …" on dev / qa — the tester sees what will be filed (2026-09-17) */
      const cur = formRef.current.projectName || "";
      if (!cur || cur === pnAuto.current) { pnAuto.current = s; if (cur !== s) Fr({ projectName: s }); }
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.divisionCode, form.targetDueDate, form.targetDeploy, form.initiative, form.reqFocus, form.community, form.masterplan, form.areaOfInterest, form.template, showTpl, markets.join(","), marketOpts]);

  /* ── AUTO-FILL FROM A COMMUNITY'S LIVE SFMC CONTENT (monolith L5355–5429) ──
     No button: when Step 1 has a community picked, quietly match it in SFMC and
     fill the selected template with its live content, narrating state in the
     green/amber banner. The user's own Studio work is never overwritten; a
     dup's real delivered HTML owns the preview. (The monolith's Dynamic-v3
     section gate/__dynReapply has no React equivalent — this is the ported
     single-community path.) */
  const [afNote, setAfNote] = useState<{ msg: string; tone?: "warn" } | null>(null);
  const autoFillCode = useRef<string | null>(null);
  const autoFilled = useRef(false);
  const dupHtmlRef = useRef("");
  dupHtmlRef.current = dupHtml;
  const afEsc = (s: unknown) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;");
  async function autoCommunityFill() {
    try {
      const raw = (formRef.current.community || "").split(",")[0].trim();
      if (!raw) { setAfNote(null); return; }
      /* ── AN ASSEMBLED ECH TEMPLATE FILLS ITSELF — DON'T SPLICE OVER IT (2026-08-20) ──
         MEASURED IN CHROME, and the reason this guard exists rather than being a
         judgement call. Division ATL → focus "Communities / Masterplans" → pick
         "1101 East Main", sampling the rail every 30ms:
             ms   33  "Community Name | 000-000-0000"   (template defaults)
             ms 4773  "1101 East Main | 770-670-2770"   (Step 1 + the division feed)
             ms 5223  "1101 East Main | 423-558-0157"   (the community's own phone)
             ms 5681  "Community Name | 000-000-0000"   ← this fill landing
         `communityFill` is a worker SPLICE onto a snapshot of the base HTML: it
         appends a live-content band and returns a static document, so it both undid
         the header fill and froze the rail against any further Step-1 edit. On an ECH
         template it is also redundant — the plans and available homes it splices are
         what ECH_Featured / ECH_ListingTable already render from the same
         /sfmc-content pack, through `echFillAll`.
         A flat catalog template still gets the splice: `asmOwns` is false there, and
         it is false on a cold library too, so the legacy path is untouched wherever
         it is still the only one that can fill anything. */
      if (asmOwns.current) { setAfNote(null); return; }
      /* a duplicated job's REAL delivered HTML owns the preview — auto-fill must
         not replace it (picking a template afterwards re-enables auto-fill) */
      if (dupHtmlRef.current || Date.now() < suppressAutoFill.current) { setAfNote(null); return; }
      /* never clobber a build the user made themselves — NO escape hatch: the
         old `&& !autoFilled.current` let a SECOND fill re-apply over a user
         build, resetting tplEdited to the fill html and collapsing
         __studio.used() to false right before submit (caught by the
         2026-08-09 live test: an edited-build submit filed as a bare intake).
         Monolith rule: once the user built, only an explicit template re-pick
         re-enables auto-fill. */
      if (studioUsed) return;
      setAfNote({ msg: "Matching <b>" + afEsc(raw) + "</b> in SFMC&hellip;" });
      const rr = await resolveCommunityName(raw);
      if (!rr) { setAfNote({ msg: "No live SFMC match for <b>" + afEsc(raw) + "</b> &mdash; using the standard template. Everything stays editable.", tone: "warn" }); return; }
      if (autoFillCode.current === rr.code) return; /* already applied */
      setAfNote({ msg: "Pulling <b>" + afEsc(rr.name) + "</b> live content &mdash; hero, floorplans &amp; available homes&hellip;" });
      const t = templates.find((x) => x.name === tplName);
      const base = previewHtml || (t ? await getTemplateHtml(t.id).catch(() => "") : "");
      if (!base) { setAfNote({ msg: "Couldn&rsquo;t pull live content for <b>" + afEsc(rr.name) + "</b> (studio not ready) &mdash; using the standard template.", tone: "warn" }); return; }
      const res = await communityFill(rr.code, base);
      if (res.ok && res.html) {
        autoFilled.current = true;
        autoFillCode.current = rr.code;
        commFromPrice.current = res.fromPrice || 0; /* monolith __commContent */
        setFillHtml(res.html); /* the fill owns the preview until the next template pick */
        onLoadStudioHtml?.(tplName, res.html); /* the STANDARD template name stays */
        setAfNote({ msg: "Auto-filled from <b>" + afEsc(res.community || rr.name) + "</b> &mdash; " + (res.plans || 0) + " floorplans &amp; " + (res.homesites || 0) + " available homes pulled live from SFMC. Anything without a live source stays as you set it &mdash; edit it all in Design Email." });
      } else {
        setAfNote({ msg: "Couldn&rsquo;t pull live content for <b>" + afEsc(rr.name) + "</b> (" + afEsc(res.err || "error") + ") &mdash; using the standard template.", tone: "warn" });
      }
    } catch { setAfNote(null); }
  }
  const firstComm = comms[0] || "";
  useEffect(() => {
    autoFillCode.current = null; /* a community change re-arms the fill (monolith L5427) */
    const t = setTimeout(autoCommunityFill, 600); /* monolith queueAutoFill debounce */
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstComm, tplName]);

  /* ── STEP 1 ↔ AUDIENCE BUILDER SYNC (monolith syncFromStep1 + the audType
     change listener, seg3 IIFE L6568–6631) ── */
  const segRef = useRef(seg);
  segRef.current = seg;
  /* Step-1 geography flows into the builder: community (when the focus is
     Communities/Masterplans) → seg.comm, AOI → seg.aoi. Read-only — never
     writes back to Step 1, so there's no sync loop. */
  useEffect(() => {
    const s = segRef.current;
    const wantComm = /community/i.test(form.reqFocus || "") && firstComm ? firstComm : "";
    const patch: Partial<SegState> = {};
    if ((s.comm || "") !== wantComm) patch.comm = wantComm;
    if (form.areaOfInterest && s.aoi !== form.areaOfInterest) patch.aoi = form.areaOfInterest;
    if (Object.keys(patch).length) setSeg({ ...s, ...patch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstComm, form.reqFocus, form.areaOfInterest]);
  /* a division switch clears picked geography — the old division's areas/
     communities don't apply (monolith __lastDiv guard) */
  const lastDivRef = useRef<string | null>(null);
  useEffect(() => {
    const now = form.divisionCode || "";
    if (lastDivRef.current !== null && now !== lastDivRef.current) {
      const s = segRef.current;
      if (s.geoInc.length || s.geoExc.length) setSeg({ ...s, geoInc: [], geoExc: [] });
    }
    lastDivRef.current = now;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.divisionCode]);
  /* the Step-1/dup audience CODE flows into the builder's checkboxes */
  useEffect(() => {
    const v = String(form.emailAudienceType || "");
    if (!/^[LORH]+$/.test(v)) return;
    const s = segRef.current;
    const next = { L: v.indexOf("L") > -1, O: v.indexOf("O") > -1, R: v.indexOf("R") > -1, H: v.indexOf("H") > -1 };
    if (JSON.stringify(next) !== JSON.stringify(s.auds)) setSeg({ ...s, auds: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.emailAudienceType]);

  /* Step-1 brief → copy-engine context (monolith __aiRead, DOM-decoupled) */
  const aiCtx = (): CopyContext => {
    /* monolith: city = the CITY half of the division label ("ATL — Atlanta" → "Atlanta") */
    const divLabel = (divisionOptions().find((d) => d.code === form.divisionCode) || { label: "" }).label;
    const city = (divLabel.split(/—|-/)[1] || "").trim();
    const dup = lastDupJob.current;
    return {
      city,
      community: comms[0],
      /* the monolith's hidden #masterplan holds the FIRST MP chip (cmSync first-of-kind) */
      masterplan: (form.masterplan || "").split(",")[0].trim(),
      aoi: form.areaOfInterest, initiative: form.initiative,
      /* the copy engine reads the human label after the "=" + the data-off flag (legacy __aiRead) */
      message: messageHuman(form.primaryMessage || ""),
      off: messageIsOffer(form.primaryMessage || ""),
      template: tplName,
      templateStage: tplStageOf(tplName),
      project: form.projectName, focus: form.reqFocus,
      buyerProfile: form.utmTarget || seg.persona, /* the requester's pick first (A2, 2026-09-08) */
      /* live pricing feeds copy only while a community is picked (monolith cc gate) */
      fromPrice: comms[0] ? commFromPrice.current : 0,
      dupText: dup ? jstr(dup, "subject") + " " + jstr(dup, "project") : "",
    };
  };

  /* monolith showPage(2) → __aiAutoSuggest(): entering Step 2 pre-fills subject +
     preheader from Step 1 — but never over a field the user has touched.
     ── AND IT FOLLOWS THE BRIEF (2026-09-10) ──────────────────────────────────────
     Re-runs while on Step 2 whenever the template, community, masterplan, division or
     primary message changes, so a template swap from the gallery re-drafts the pair.
     Still only ever overwrites a field that is empty or holds the engine's own last
     draft — a typed or duplicated value stays. A field still carrying the retired
     webinar seed (drafts saved before today) counts as untouched, so it self-heals. */
  useEffect(() => {
    if (step !== 2) return;
    try {
      const c = draftCopy(aiCtx());
      const patch: Partial<FormState> = {};
      /* clamped like the inputs are (audit 2026-08-24): `maxLength` only covers typing,
         so an AI draft or a duplicated job could still put an over-length subject into
         state and read "112 / 80" — the very state the enforcement was added to stop */
      const curS = form.subject || "", curP = form.preheader || "";
      if (!subjTouched.current && (!curS || curS === subjAuto.current || curS === LEGACY_SEED_SUBJECT)) {
        patch.subject = String(c.subjects[0].text || "").slice(0, SUBJ_HARD);
        subjAuto.current = patch.subject;
      }
      if (!preTouched.current && (!curP || curP === preAuto.current || curP === LEGACY_SEED_PREHEADER)) {
        patch.preheader = String(c.preheader || "").slice(0, PRE_HARD);
        preAuto.current = patch.preheader;
      }
      if (Object.keys(patch).length) F(patch);
    } catch { /* engine must never block navigation (monolith try/catch) */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, tplName, form.community, form.masterplan, form.divisionCode, form.primaryMessage]);

  /* full dupApply port (monolith L5237–5333): focus + geography mirroring (with
     the geo-from-initiative fallback for old jobs), deploy date/time, derived
     message + senderType, the step-3/4 fields, the inferred nurture template —
     applied only if no genuine send exists — and the /sent-email pull that loads
     the ACTUAL delivered email into the Creative Studio. */
  function applyJob(job: DupJob) {
    const g = (k: string) => jstr(job, k);
    /* ── A DUPLICATE REPLACES WHATEVER DOCUMENT WAS THERE (2026-08-29) ────────────
       AZH, twice in three minutes: "the preview after being submitted doesn't match
       what was submitted" and "preview not populating for a duplicate job, still
       just showing template unless i click into creative studio."

       MEASURED on dev, same Riverwalk job duplicated two ways:
         · on a fresh form            → "Saturday, August 29th 11 am–1 pm", the
                                        delivered email
         · after opening the Studio   → "Saturday, July 19th 10 am-2 pm", Layne's
                                        Grand Opening Invite DEFAULTS
       The rail is `liveDocHtml || editedHtml || dupHtml || …` and the document leads
       it — correctly, the document IS the email (2026-08-24). But once any document
       exists, it outranks the duplicate the requester just chose, and the preview
       shows template content with the right community name on it, which reads as
       "not populating" rather than as the wrong document.

       `onResetDocument` already exists for exactly this — #69 wired it to starting
       another request and to picking a different template, and a duplicate load is
       the same class of event: whatever was on the canvas has nothing to do with the
       job being duplicated. It was left out because "a duplicate stays fully
       revisable" (the note on the prop), but that is about keeping the DUPLICATE
       editable afterwards, which this does not touch — the manifest path below loads
       the duplicate's real blocks immediately after, and the flat path leaves
       `dupHtml` leading an empty document. Editing after the load still wins, which
       is the ATL fix from the same day and must not regress. */
    onResetDocument?.();
    /* Synchronously, BEFORE the async load below: `dupHtml` now SHIPS (it is in the
       submit precedence), so a stale value is no longer a preview flash — it is the
       delivered creative. Without this, duplicating job A and then job B whose
       /sent-email fails (or is slow, with no community to fill from) submits job A's
       email under job B's form fields (evaluator R2, 2026-08-31). */
    setDupHtml("");
    lastDupJob.current = job; /* monolith __lastDupJob — feeds the AI copy blob */
    suppressAutoFill.current = Date.now() + 20000; /* cover the gap until the delivered HTML loads */
    setDupOpen(false);
    const div = g("division") || form.divisionCode;
    /* mirror the old job's focus + geography exactly; derive focus from whichever
       geo the job has when the focus field itself is blank */
    const focus0 = g("focus") || (g("community") ? "Community" : g("masterplan") ? "Masterplan" : g("aoi") ? "Area of Interest" : "");
    let focus = focus0 === "Masterplan" ? "Community" : focus0; /* merged option — MP jobs land on Communities/Masterplans */
    let community = g("community") && g("community") !== "Market-level" ? g("community") : "";
    let masterplan = g("masterplan");
    let aoi = g("aoi");
    if (!community && !masterplan && !aoi && g("initiative")) {
      /* older cached jobs: pull the geo out of the initiative label ("… for Diamond Springs - Ongoing") */
      const im = g("initiative").match(/\bfor\s+(.+?)\s*(?:-\s*(?:Ongoing|\d)|$)/i);
      const geo = ((im && im[1]) || "").trim();
      if (geo && !/national|jacksonville|division/i.test(geo)) {
        if (divComms(div).indexOf(geo) > -1) { community = geo; if (!g("focus")) focus = "Community"; }
        else if (noClosed(MASTERPLANS).indexOf(geo) > -1) { masterplan = geo; if (!g("focus")) focus = "Community"; } /* merged option */
        else if ((refAois.length ? refAois : AOIS).indexOf(geo) > -1) { aoi = geo; if (!g("focus")) focus = "Area of Interest"; }
      }
    }
    /* deploy date + time out of the job's deploy stamp */
    const dm = g("deploy").match(/\d{4}-\d{2}-\d{2}/);
    const tm = g("deploy").match(/(\d{1,2}:\d{2})/);
    /* ── A COPY NEVER INHERITS A DATE THAT HAS PASSED (2026-08-27) ───────────────
       Duplicate copies a job that has already DEPLOYED (dupJobs.pastOnly), so its
       deploy stamp and due date are past by construction — this is the likeliest
       source of Layne's three reports, because the copy arrives looking filled in and
       gets clicked through. Blank is the honest prefill: the requester picks the new
       date on purpose. Everything else about the copy is unchanged. */
    /* ── AND NOW NEVER INHERITS A FUTURE ONE EITHER (2026-10-29, Jack) ───────────
       Resubmit now offers a job as soon as it is submitted, before it sends, so its
       date can still be ahead — and a copy landing on the original's send date by
       accident is the failure. The copy always starts blank; the "had passed" note
       still names a date that really had passed. `ddRaw` still steers the sent-email
       lookup below (the send nearest the job's own date). */
    const ddRaw = dm ? dm[0] : "";
    const dd: string = "";
    const dt = tm ? tm[1] : depTime;
    const dropped: string[] = [];
    if (ddRaw && !dropPastDate(ddRaw)) dropped.push("deployment date");
    if (dd) setDepDate(dd);
    else if (ddRaw) { setDepDate(""); writeDeploy("", dt); }
    if (tm) setDepTime(tm[1]);
    /* derive message + senderType from the job text; richer live fields win */
    const msg = g("message") ? messageCode(g("message"), MESSAGES) : deriveMessage(job);
    const senderType = g("senderType") || ((focus || "").toLowerCase().indexOf("community") > -1 ? "Community" : "Market");
    const patch: Partial<FormState> = { divisionCode: div, senderType };
    if (g("market")) patch.market = [g("market")];
    else if (div !== form.divisionCode) { const dmk = divMarkets(div); patch.market = dmk.length ? [dmk[0][0]] : []; }
    /* an initiative whose run has ended is not copied — the field is left BLANK and the
       requester is told, exactly as the past dates are handled below (2026-09-08) */
    if (g("initiative") && initiativeEnded(g("initiative"))) { patch.initiative = ""; keepInitRef.current = ""; dropped.push("initiative"); }
    else if (g("initiative")) { patch.initiative = g("initiative"); keepInitRef.current = g("initiative"); }
    if (focus) patch.reqFocus = focus;
    if (aoi) patch.areaOfInterest = aoi;
    if (masterplan) patch.masterplan = masterplan;
    if (community) patch.community = community;
    if (g("project")) patch.projectName = g("project") + " (copy)";
    /* the job's own copy is the requester's copy — the Step-2 engine must not redraft it (A3) */
    if (g("subject")) { patch.subject = String(g("subject")).slice(0, SUBJ_HARD); subjTouched.current = true; }   /* same clamp as the input */
    if (g("pre")) { patch.preheader = String(g("pre")).slice(0, PRE_HARD); preTouched.current = true; }
    if (dd) patch.targetDeploy = dd + " " + dt;
    else if (ddRaw) patch.targetDeploy = "";
    /* RESUBMIT (2026-10-13): the old job's request type is NOT copied. On an app job it reads
       "Creative Hub Email" (Katy's automation sets it), which is not a form option, and an
       unknown select value fails the Airtable write. The form keeps its default until
       RESUBMIT_REQ_TYPE is set to the value Digital Ops and Katy choose. */
    if (RESUBMIT_REQ_TYPE) patch.requestType = RESUBMIT_REQ_TYPE;
    if (msg) patch.primaryMessage = msg;
    if (g("senderProfile")) patch.senderProfile = g("senderProfile");
    if (g("specialty")) patch.specialtyBranding = g("specialty");
    /* ── A DUPLICATE DOES NOT ROUND-TRIP DISCLAIMER JUNK (2026-08-31, Farhan) ─────────
       This line used to copy the old row's value verbatim, which is the path that produced
       "DB-2026-0909-JAX-GP50-FP": junk filed once got recycled into every duplicate forever.
       Normalised on the way in — a clean number (with or without a #/DB prefix) survives,
       anything else is dropped AND REPORTED through the same `dropped` list the due date
       already uses, so the requester sees it went missing instead of discovering it later. */
    if (g("disclaimer")) {
      const dn = disclaimerId(g("disclaimer"));
      if (dn) patch.disclaimer = dn;
      else dropped.push("disclaimer request # (wasn't a number)");
    }
    if (g("anythingElse") || g("notes")) patch.anythingElse = g("anythingElse") || g("notes");
    if (g("audType")) patch.emailAudienceType = g("audType");
    /* ── THE SEND SCOPE COMES WITH THE JOB (2026-09-08, pre-UAT e2e A6) ─────────────
       /jobs now carries the Salesforce lists and the buyer type; a duplicate that
       copied the template and the message but not who it went to was a request the
       requester had to rebuild from Step 3 down. Buyer Demo (utmTarget) is derived
       from the prod multi-select: one of the app's five persona values when the job
       names exactly that buyer type, otherwise the pick stays blank — a guess here
       would ship a wrong UTM, which is the A2 class of bug in reverse. */
    if (g("sfInclude")) patch.sfListsInclude = g("sfInclude");
    if (g("sfExclude")) patch.sfListsExclude = g("sfExclude");
    if (g("buyerType")) {
      const bt = g("buyerType").split(",").map((x) => x.trim().toLowerCase()).filter(Boolean);
      const map: Record<string, string> = { "first-time homebuyer": "FTHB", "move-up buyer": "MoveUp", "active adult/55+": "ActiveAdult", investor: "Investor", "all buyer types": "Market" };
      const hits = bt.map((x) => map[x]).filter(Boolean);
      if (hits.length === 1) patch.utmTarget = hits[0];
    }
    if (g("dueDate")) {
      /* blank like the send date (2026-10-29); the note only for one that had passed */
      patch.targetDueDate = "";
      if (!dropPastDate(g("dueDate"))) dropped.push("due date");
    }
    /* ── THE NURTURE TEMPLATE RIDES WITH THE MESSAGE (2026-08-28, ATL) ───────────
       Reported as "the email nurture template stays defaulted to Coming Soon and
       the primary message was Grand Opening." Both fields describe one email, and
       they disagreed because they were written in different places: the message
       lands here, unconditionally, while the template was applied further down —
       AFTER the two `return`s in the /sent-email branch below. So on the GOOD path
       (a genuine sent email is on file) the template was never written at all and
       the form's default stood.

       Applying it in this patch is safe now: the 2026-07-23 flash guard that pushed
       it down there was about the PREVIEW briefly showing a library template before
       the real send resolved, and the rail is guarded independently — `asmOwns` for
       the assembled render, and `dupHtml`/`liveDocHtml` ahead of `previewHtml` in
       the chain. Step 1's field is not the preview.

       Falls back to the STAGE the message implies, so the two can no longer
       contradict each other even when the job text matches no template name. */
    const tplChoices = mvpTemplateChoices(echTplFor);
    const tnRaw = dupPickTemplate(job, tplChoices);
    const tnDup = (tnRaw && echTplFor(tnRaw)) || tnRaw;
    const tnFromMsg = !tnDup && msg
      ? tplChoices.find((t) => tplStageOf(t) === tplStageOf(messageText(msg))) || ""
      : "";
    const tn = tnDup || tnFromMsg;
    if (tn) patch.template = tn;
    F(patch);
    if (patch.initiative) setInitiatives((prev) => (prev.indexOf(patch.initiative as string) > -1 ? prev : [...prev, patch.initiative as string]));
    goStep(1); /* monolith goPage(1) */
    notify("Copied from “" + (g("project") || "job") + "” — review before submitting."
      + (dropped.length ? " The old " + dropped.join(" and ") + " had passed — pick "
        + (dropped.length > 1 ? "new ones" : "a new one") + "." : ""));
    /* The template was chosen and APPLIED in the patch above (2026-08-28). It used
       to be applied down in the no-genuine-send branch only, to prevent a
       template→real preview flash (2026-07-23); the rail now guards that itself, and
       leaving Step 1's field unwritten was ATL's report.

       CANONICALISED TO THE SFMC NAME (2026-08-24). `dupPickTemplate` matches an old job's
       text against the five gallery-era names, and several of those are also
       /templates.json rows — so handing one back put a legacy name in the picker and let
       the stored catalog HTML answer the rail again. `echTplFor` maps it to the live SFMC
       template the alias stands for; if the library hasn't loaded, the raw name is still a
       safe placeholder because the rail guard above refuses the baked-in email. */
    (async () => {
      /* FIRST CHOICE (Resubmit, 2026-10-13): an app job's OWN submitted email, by its SFMC
         asset id. It carries the block record, so it reopens as real blocks — no subject
         guessing. SECOND: the GENUINE sent email by subject, as before. */
      try {
        let sentHtml = "", fromSubmit = false;
        if (g("assetId")) {
          const h = await getSubmittedHtml(g("assetId")).catch(() => "");
          if (lastDupJob.current !== job) return;
          if (h && h.length > 500) { sentHtml = h; fromSubmit = true; }
        }
        if (!sentHtml && (g("subject") || g("project"))) {
          const sj = await getSentEmail({ subject: g("subject"), div: g("division"), date: ddRaw || undefined, name: g("project"), rec: g("id") });
          /* STALENESS TOKEN (2026-08-31): `dupHtml` ships now, so a slow load that
             resolves after a NEWER duplicate was applied must not land — it would
             ship THIS job's creative under the newer job's form fields. The sync
             clear at the top of applyJob only covers loads that already finished. */
          if (lastDupJob.current !== job) return;
          if (sj && sj.ok && sj.html && sj.html.length > 500) sentHtml = sj.html;
        }
        if (sentHtml) {
          const sj = { html: sentHtml };
          const asLabel = (fromSubmit ? "As submitted · " : "As sent · ") + (g("project") || g("subject"));
          if (sj.html.length > 500) {
            suppressAutoFill.current = Date.now() + 20000; /* dup's own pick must not re-enable auto-fill */

            /* ── THE RECIPE, IF THE ORIGINAL CARRIED ONE (2026-08-20) ────────────
               A sent email built in this Studio has its own block list stamped into
               it. When it does, the duplicate comes back as REAL BLOCKS — governed
               by today's guardrails, editable field by field, and re-fillable from
               the live SFMC feeds. Without the recipe we are back to loading one
               opaque document, which is what every pre-2026-08-20 send and every
               email built outside the Studio still gets.

               BOTH RECOVERIES RUN, ALWAYS, AND THE REQUESTER PICKS NOTHING (per Jack:
               "could it not do both as a straight process — the requester will never
               make any of this decision"). `duplicateFromHtml` fills from the recipe
               and independently re-reads the delivered markup as a checksum; the only
               thing that reaches the screen is what the two disagree about, plus
               anything that changed in Content Builder since the send. */
            const dup = duplicateFromHtml(sj.html);
            if (dup.ok && dup.blocks.length && onLoadStudioBlocks) {
              onLoadStudioBlocks(asLabel, dup.blocks, dup.notices);
              setDupHtml(""); /* the block canvas is the document now — no flat preview */
              notify(dup.notices.length
                ? "Rebuilt from the original request — " + dup.notices.length + " thing" + (dup.notices.length > 1 ? "s" : "") + " to check in Design Email."
                : "Rebuilt from the original request — every block is editable in Design Email.");
              return;
            }

            setDupHtml(sj.html);
            onLoadStudioHtml?.(asLabel, sj.html);
            notify(fromSubmit
              ? "Loaded the email as it was submitted — edit any text or image in Design Email."
              : "Loaded the actual sent email — edit any text or image in Design Email.");
            return; /* genuine send found — skip the library best-match */
          }
        }
      } catch { /* fall through to the fallback template */ }
      /* NO genuine sent email on file. The template is already set (in the patch
         above) — never fake an "As delivered" label either (2026-07-22, per Jack).
         Don't leave placeholders gray: recreating a community job is
         explicit intent — fill the template with THAT job's community */
      const dc = community || masterplan; /* masterplan jobs fill too (Shearwater case) */
      if (!dc) return;
      try {
        const rr = await resolveCommunityName(dc);
        if (!rr) return;
        const useTpl = tn || tplName;
        const t = templates.find((x) => x.name === useTpl);
        if (!t) return;
        const base = await getTemplateHtml(t.id).catch(() => "");
        if (!base) return;
        suppressAutoFill.current = 0; /* this fill IS the intent */
        const res = await communityFill(rr.code, base);
        if (lastDupJob.current !== job) return; /* superseded by a newer duplicate */
        if (res.ok && res.html) {
          commFromPrice.current = res.fromPrice || 0; /* monolith __commContent */
          setDupHtml(res.html);
          onLoadStudioHtml?.((g("project") || dc) + " · filled", res.html);
          notify("No archived send for this job — filled the template with " + dc + " instead.");
        }
      } catch { /* fill is best-effort */ }
    })();
  }

  async function submit() {
    /* the synchronous half of the duplicate guard — see `sending` above. Released in
       every exit path below, so a validation failure never wedges the button. */
    if (sending.current) return;
    /* ── A PAST DATE BLOCKS THE SUBMIT (2026-08-27) ───────────────────────────────
       Layne: "there's 3 records of feedback around being able to select dates that
       have passed." Checked BEFORE the completeness gate and reported separately: the
       field is filled, so "complete the highlighted fields" would have been a lie and
       the requester would have stared at a valid-looking date. Named field, named
       problem, jump to the page it lives on. */
    const past: Array<[string, number]> = [];
    if (isPastDate(form.targetDueDate)) past.push(["targetDueDate", 1]);
    if (isPastDateTime(depDate, depTime)) past.push(["deployDate", 2]);
    if (past.length) {
      setMissing(new Set(past.map(([k]) => k)));
      setTouchedSteps((t) => { const x = new Set(t); past.forEach(([, p]) => x.add(p)); return x; });
      setStep(past[0][1]);
      setNoteMsg(past.map(([k]) => PAST_DATE_MSG[k]).join(" "));
      window.setTimeout(() => {
        const fm = bodyRef.current?.querySelector(".f.fmiss");
        if (fm) fm.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 70);
      return;
    }
    /* monolith submit gate (btnSubmit onclick): validate all four pages first */
    const bad = [1, 2, 3, 4].filter((p) => !pageComplete(p)).sort((a, b) => a - b);
    if (bad.length) {
      const m = new Set<string>();
      bad.forEach((p) => reqPairs(p).forEach(([k, v]) => { if (String(v || "").trim() === "") m.add(k); }));
      setMissing(m);
      setTouchedSteps((t) => { const s = new Set(t); bad.forEach((p) => s.add(p)); return s; });
      setStep(bad[0]);
      /* monolith pluralizes by the count of bad PAGES, not fields (L5200) */
      setNoteMsg(`Complete the highlighted field${bad.length > 1 ? "s" : ""} before submitting — starting with section ${bad[0]}.`);
      window.setTimeout(() => {
        const fm = bodyRef.current?.querySelector(".f.fmiss");
        if (fm) fm.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 70);
      return;
    }
    /* ── THE STUDIO'S REQUIRED FIELDS GATE THE SUBMIT TOO (Jack, 2026-08-22:
       "you cannot send an eblast unless all of the Required before Done in studio
       is completed") ──────────────────────────────────────────────────────────
       `requiredBlanks` already backs the Studio's own "N required missing" chip
       and the per-card link markers — it was simply never consulted here, so a
       division could leave the Studio with 14 blanks showing and still submit.
       Reusing the SAME function is the point: the Studio and the submit can never
       disagree about what's missing, the way the card and the Done gate already
       can't (Inspector L617).

       Used to fire only when the division had opened the Studio — "an untouched
       template has nothing of theirs to be missing". Since 2026-08-25 the untouched
       template IS the email that ships (one document, shown and sent), so a blank in
       it is a blank in a live send whoever left it there.
       ── EVERY ECH DOCUMENT IS GATED (2026-09-10, TLA: "I didn't select a main image
       and the form still let me submit"; Jack: hard block) ────────────────────────
       The belt reads the FILLED document the rail shows (`liveDoc.doc`), not App's raw
       `blocks`: on the form-only path the raw blocks still carry the shipped bare
       "https://" on every fed link, which the fill is about to write — flagging those
       would block every requester who never opened the Studio. `feedPending` covers the
       same race the Studio's Done click now waits on. */
    const gated = liveDoc.doc.length ? (liveDoc.doc as unknown as AnyBlockInstance[]) : blocks;
    /* the DATA gate belts here too (2026-09-09, Layne's no-row ask) — a no-row division,
       an empty mandatory column or a fed value that fails its pattern. Same list, same
       reason the Studio's own blanks are consulted here: the two must never disagree. Read
       off the same FILLED document as the required gate (2026-09-11 rebase). */
    const studioMissing = [
      ...(gated.some((b) => isEch(b.type)) ? requiredBlanks(gated, { feedPending: !!echCtx.pending }) : []),
      ...dataGateBlanks(gated, echCtxRef.current),
    ];
    if (studioMissing.length) {
      const n = studioMissing.length;
      /* name the blocks, not the field keys — a division reads "Main Image" and
         "Homesite Listing", never `imgSrc` or `home3Url` */
      const where = Array.from(new Set(studioMissing.map((r) => r.block))).slice(0, 3);
      setNoteMsg(
        "Your email still needs " + n + " required " + (n === 1 ? "item" : "items") +
        " — " + where.join(", ") + (where.length < new Set(studioMissing.map((r) => r.block)).size ? " and more" : "") +
        ". Open Design Email to finish, then submit.",
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    /* monolith flow: the DONE view shows IMMEDIATELY (request still in flight),
       then the step cards move as the verdict and the live stage arrive. */
    const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
    const snap = segToAudienceSnapshot(seg);
    /* ── THE BUYER TYPE IS WHAT THE REQUESTER PICKED, NOT THE BUILDER'S DEFAULT
       (2026-09-08, pre-UAT e2e A2: 13 of 13 payloads filed "First-time Homebuyer"
       while the UTM in the same payload carried the real pick). "Buyer Demo for
       Targeting" (form.utmTarget) is the required, visible choice; seg.persona is
       the audience builder's internal state, which SEG_INIT seeds to "FTHB" and the
       builder→form sync only ever writes one way. So the column read a default the
       requester never saw. The pick wins whenever it is set; the builder persona is
       the fallback for a payload that somehow has none. */
    if (form.utmTarget) snap.buyerProfile = form.utmTarget;
    /* monolith reqFocusOut() (L4151): a Communities/Masterplans focus with
       ONLY masterplan chips files as "Masterplan" */
    const focusOut = reqFocus === "Community" && mps.length && !comms.length ? "Masterplan" : reqFocus;
    /* monolith build() — the naming engine runs on every submit */
    const naming = buildNaming({
      divisionCode: form.divisionCode, market: markets[0], community: comms[0],
      messageCode: form.primaryMessage, initiative: form.initiative,
      deployDate: depDate, audType: form.emailAudienceType, utmTarget: form.utmTarget,
    });
    setDoneName(naming.name);
    /* a fresh submit starts from no verdict and no live row — otherwise the
       previous request's Filed/Received/stage would show for a second */
    setDoneFiled("pending");
    setDoneRec("");
    setDoneStored(false);
    setDoneStage("");
    setDonePrevH(0);
    /* and no previous email: the filed one is set below, after the license line resolves */
    setDoneHtml("");
    sending.current = true;
    /* ── AND IT IS RELEASED NO MATTER HOW THIS EXITS (2026-08-28) ─────────────────
       Caught by the test: releasing the flag on the last line means ANY throw between
       here and there — a rejected mirror-revise, a failed stage poll — leaves it set
       and the Submit button dead for the rest of the session, which is a worse bug
       than the duplicate it was added to prevent. `finally` is the whole point. */
    try {
    /* a NEW id per attempt: a retry after an error is a different attempt and must be
       distinguishable from the first one */
    const attempt = "ech-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
    setSubmitState("busy");
    window.scrollTo({ top: 0, behavior: "smooth" });
    let ok = true, recId = "", stored = false, delivered = true;
    /* what proofing needs to open version 1 of this job (2026-10-06, M2): the SFMC
       asset id is the job key, and the hosted HTML is what the division submitted */
    let filedAsset = "", filedHtml = "";
    /* the destinations the worker says refused — surfaced on the done screen */
    let problemWheres: string[] = [];
    /* the reason a submit did not file, for the failed screen (2026-09-22) */
    let failMsg = "";
    setSubmitMsg("");
    /* THE EMAIL LEAVES FOR SFMC HERE, so here — and only here — the listing
       table's baked prices become a send-time lookup (SOLD included, was/now
       excluded, per Jack 2026-08-21). Every preview surface keeps the baked
       snapshot: raw %%[ ]%% between <tr>s renders as stray text in a browser. */
    /* ── WHAT SFMC GETS IS WHAT THE RAIL SHOWED (2026-08-25) ─────────────────────
       Was: `studioUsed ? sendTimeListing(builtHtml, blocks) : ""`. Two faults in one
       line, both reported by Uche with screenshots of an SFMC asset full of
       placeholders:
         · `studioUsed` is App's STARTER-divergence test, so a request built entirely on
           the FORM sent no creative at all and SFMC assembled the bare template;
         · `builtHtml` is App's raw render of the block state — the requester's field
           values, but none of the fill or the card expansion the rail applies.
       Both consumers now read the SAME filled document: the live one when the Studio
       has a doc, otherwise the template assembled with this request's own context —
       byte-identical to what the requester was just looking at. The old guard'"'"'s real
       job is kept: the STARTER mock is not an ECH document, so it can never be sent. */
    /* ── PRECEDENCE, CORRECTED AFTER AN AUDIT (2026-08-25) ────────────────────────
       The first version fell straight from "no ECH doc" to the ASSEMBLED TEMPLATE, which
       silently broke every FLAT-EDITOR flow: duplicate-a-sent-email, a division
       favorite, the community auto-fill and any loaded template edit all leave `blocks`
       as App's STARTER mock (legacy types, so `liveDoc` is empty) while the real email
       lives in `builtHtml` (`tplEdited`). Those submits would have shipped a standard
       template in place of the email the requester had loaded and edited — and the rail
       would still have shown the right one, so nobody would have caught it.
       Order now: the ECH document if there is one · else whatever the requester actually
       built or loaded (the OLD behaviour, untouched) · else — and only else, which is the
       case that used to send NOTHING — the template assembled with this request's
       context. */
    /* ── THE DUPLICATE IS PART OF THE PRECEDENCE (2026-08-31) ─────────────────────
       The flat duplicate, the ★ favorite pick and applyJob's community-fill
       fallback park their delivered email in `dupHtml` — which fed ONLY the
       preview. (`autoCommunityFill`'s `fillHtml` is a separate lane, still
       preview-only, near-dead in the MVP world where every template is ECH and
       `asmOwns` stands the fill down.) After #74 (correctly) made
       applyJob reset the canvas, `studioUsed` is deterministically false on a flat
       duplicate, so every such submit fell through to the ASSEMBLED, WORD-SCORE-
       GUESSED template: the rail showed job X's real email, the payload carried a
       generic Coming Soon, and the Done screen displayed the same wrong thing
       (measured live: 'Explore Ripon' → 42KB 'Coming Soon' assembly, 2026-08-31).
       The duplicate slots BELOW the Studio branches — an ECH doc or a Studio edit
       still outranks it — and ABOVE the template fallback it was losing to.
       `doc: []` on purpose: no visual spec ships for an opaque as-sent document,
       which is the documented pre-2026-08-20 bare-intake+asset payload shape —
       strictly better than filing the guessed template's Block Order/Build Spec. */
    const outDoc = liveDoc.doc.length
      ? liveDoc
      : (studioUsed && builtHtml
        ? { html: builtHtml, doc: blocks as unknown as DynBlock[] }
        : (dupHtml
          ? { html: dupHtml, doc: [] as DynBlock[] }
          : (echTplFor(tplName) ? echPreviewForOf(tplName, echCtx) : { html: "", doc: [] as DynBlock[] })));
    /* A doc-less branch still ships its HTML — gating the payload on `doc.length`
       is how the duplicate fix would have shipped an EMPTY creative behind a green
       Done screen (evaluator R1). The final fallback stays "" by construction. */
    /* THE FILED DISCLAIMER WAITS FOR /refdata (Farhan's review of #396, 2026-10-23): the
       broker / license line is rebuilt here from the communities' own states, so what gets
       filed never depends on how fast the request was submitted. */
    if (dsOn) await resolveLegalOptions();
    const fileParas = dsOn
      ? dsAssemble(dsDet, { ...dsAns, requestNo: dsReqNo }, { division: form.divisionCode, deploy: form.targetDeploy, license: await resolveLicenseLine(form) })
      : dsParas;
    /* every US `tel:` link dials as +1; the visible number is untouched (Layne 10-27,
       services/telLinks.ts) */
    const outHtml = telLinksPlusOne(dsApply(outDoc.doc.length
      ? sendTimeListing(outDoc.html, outDoc.doc as unknown as AnyBlockInstance[])
      : outDoc.html, fileParas));
    /* the review pane shows the BAKED document, not `outHtml`: identical creative,
       minus the send-time listing lookup that a browser prints as stray text */
    setDoneHtml(sendTimeView(dsApply(outDoc.html || "", fileParas), outDoc.doc as unknown as AnyBlockInstance[]));
    try {
      /* ── BAKED IMAGES ARE HOSTED BEFORE THE EMAIL LEAVES (2026-09-16) ─────────────────
         A hero slideshow GIF or an overlay-text hero exists only in this tab as a data: URL.
         Each distinct one is uploaded once to Content Builder (/sfmc-image) and swapped in the
         HTML and the block doc; if SFMC refuses, the submit stops here rather than filing an
         email whose hero no inbox can load. services/hostImages.ts */
      const imgName = (naming.name || form.projectName || "ECH_Hero").replace(/[^A-Za-z0-9_-]+/g, "_");
      /* ── EVERY FEATURED CARD PHOTO AT THE CARD'S FRAME (2026-09-25) ─────────────────────
         A lennar.com photo with no Bynder original is cropped to the card's frame and
         hosted like the GIF hero; one that can't be read or hosted ships as it was, so this
         can never stop a submit. services/cropCardPhotos.ts */
      const cards = await cropCardPhotos(outHtml, outDoc.doc as unknown as AnyBlockInstance[], imgName);
      let doneBase = dsApply(outDoc.html || "", fileParas);
      cards.map.forEach((to, from) => { doneBase = pinCardImg(doneBase, from, to, cards.heights.get(from) || 180); });
      if (cards.cropped) setDoneHtml(sendTimeView(doneBase, outDoc.doc as unknown as AnyBlockInstance[]));
      const hosted = await hostDataImages(cards.html, cards.doc, imgName);
      if (hosted.failed.length) throw new Error("image hosting failed: " + hosted.failed.join("; "));
      filedHtml = hosted.html;
      if (hosted.hosted) setDoneHtml(sendTimeView(swapInStr(doneBase, hosted.map), outDoc.doc as unknown as AnyBlockInstance[]));
      /* THE BUILT DISCLAIMER REACHES AIRTABLE (2026-10-14, Katy): the same paragraphs written
         into the footer above (the one-line Detail was dropped 2026-10-23, Katy). Only when the
         email actually has the footer block — otherwise nothing was built into it. */
      const dsInEmail = outHtml.indexOf("<!--BEGIN Com disclaimer-->") >= 0;
      const dsOut = dsOn && dsInEmail ? { dsText: fileParas.join("\n\n") } : {};
      const body = buildSubmitBody({ ...form, ...dsOut, reqFocus: focusOut, emailName: naming.name, utm: naming.utm }, {
        ...snap,
        community: comms[0] || snap.community || "Market-level (pre-launch)",
        audType: form.emailAudienceType || snap.audType,
      },
        hosted.html,
        /* THE BLOCK DOC, gated on the same studioUsed rule as the HTML (2026-08-20).
           The visual spec describes a build the user actually made; sending it for an
           untouched STARTER doc would file the webinar starter blocks as though the division
           had chosen them — the same class of bug the builtHtml gate below exists to prevent. */
        hosted.doc,
        /* the division's masterplans, so a masterplan name derived from a collection pick
           ships only when it is a real one (Katy, UTH 2026-09-08: "not a name without an ID") */
        { masterplans: refMps }); /* the SAME doc as the html above —
        the visual spec has to describe the creative that was actually sent, and the
        STARTER mock cannot reach here because it is not an ECH document (2026-08-25).
        Superseded reasoning below, kept for the defect it records:
        monolith: Custom Build only when __studio.used() —
        unconditional builtHtml attached the STARTER-BLOCK mock as the creative HTML on
        no-studio submits (caught by the 2026-08-09 live test submit: asset 1027583 held
        the 7KB webinar starter render instead of "build from template") */
      /* the attempt id rides in the ENVELOPE, not in a mapped field: the worker writes a
         whitelist of Airtable field ids, so an extra key is inert there and safe to add,
         while still reaching the intake webhook payload where Katy can see it */
      body.requestId = attempt;
      const r = await submitRequest(body);
      const loose = r as { ok?: boolean; error?: string; airtableRecordId?: string; recordId?: string; customHtmlStored?: boolean;
        asset?: { assetId?: number | string } | null; delivered?: boolean; problems?: { where?: string; detail?: unknown }[];
        katyHook?: { status?: number; deferred?: string; error?: string; skipped?: string } | null };
      /* success is EXPLICIT: `ok === true` and no error. `r.ok !== false` treated a missing
         field as success, which is how a gateway body with no `ok` filed as a request
         (RSK-1977, Farhan 2026-09-22). submitRequest already refuses a body without the
         boolean; this is the second lock on the same door. */
      ok = r.ok === true && !loose.error;
      /* ── "FILED" NOW MEANS IT REACHED THE PEOPLE WHO ACT ON IT (2026-08-25) ────────────
         The legacy row landing is necessary but not sufficient: the worker also writes the
         mirror row and posts Katy's intake webhook, and until today BOTH could fail while
         this screen still printed "Filed in Airtable". With `typecast:false` one bad field
         name fails the whole 144-field write, so the division saw success and Katy's table
         got nothing — for a week, because nothing surfaced it.
         `delivered` is the worker's new verdict. Treated as TRUE when absent so an older
         worker (or a cached one mid-deploy) keeps the previous behaviour rather than
         alarming on every submit. */
      delivered = loose.delivered !== false;
      if (!delivered) console.warn("submit reached Airtable but not everywhere:", loose.problems);
      /* the destinations that refused, for the done screen — see `doneProblems` */
      problemWheres = (loose.problems || [])
        .map((p) => String((p && p.where) || "").trim())
        .filter(Boolean);
      recId = loose.airtableRecordId || loose.recordId || "";
      stored = !!loose.customHtmlStored;
      filedAsset = String((loose.asset && loose.asset.assetId) || "");
      if (!ok) failMsg = String(loose.error || "the backend refused the request");
      /* ── NO RECORD ID IS NOT A FILED REQUEST (2026-09-22) ──────────────────────────
         The backend answers every filed request with the Airtable record id it just
         wrote (exko.ramey's 2:52 PM row on 2026-09-22 carries one; so does every row in
         the intake table). A 200 with `ok:true` and no id means the request was accepted
         and NOT written — the dev backend returned exactly that the same day
         (`ok:true, wrote:17, airtableRecordId:null`, no row anywhere). Until now that
         rendered as "Request submitted"; it is a failure, and it says so. */
      /* ── …UNLESS THE INTAKE HOOK TOOK IT (2026-09-23, correction) ───────────────────
         The rule above was half right. Since BE #49 (webhook-only intake, in PROD from
         09-09) the backend writes no Airtable row of its own on ANY environment where
         SANDBOX_AIRTABLE is off, so `airtableRecordId` is null on EVERY filed request —
         the request is filed by the POST to Katy's intake hook, and the response says so
         in `katyHook`: `{status:200}` when the hook accepted it, `{deferred}` when the
         backend will make that one post once the division ids resolve. Reading "no id"
         as "not filed" on those answers told a requester her filed request failed and
         kept her draft — and Try again files it a second time (a new Content Builder
         asset and a second intake row). So a filed request is `ok:true` AND (a record id
         OR the hook took it OR the hook post is scheduled). A hook that was never
         configured, errored or refused, with no id, is still not filed. */
      const hook = loose.katyHook || null;
      const hookTook = !!hook && ((typeof hook.status === "number" && hook.status >= 200 && hook.status < 300) || !!hook.deferred);
      if (ok && !recId && !hookTook) { ok = false; failMsg = "the backend answered without a record id — nothing was filed"; }
      if (!ok) console.warn("submit error:", failMsg);
      /* the job just filed has to be findable in Duplicate A Past Job — both cache
         layers are keyed by division and neither expired on a submit (2026-08-28) */
      if (ok) dupInvalidate(form.divisionCode || "");

      /* THE CREATIVE MUST NOT SILENTLY VANISH. /sfmc-submit stores small
         custom-html builds but refuses ~100KB template-mode docs — it answers
         ok:true with customHtmlStored:false and no error, so a template-edit
         request would file with NO build attached and Creative would open the
         job to nothing. When that happens, re-file the same HTML through
         /mirror-revise (create-mode), which handles 100K+ docs today; it makes
         the TEST_ Content Builder asset + hosted preview and stamps the row.
         Measured 2026-08-09: 96KB refused by submit, stored by this route. */
      /* the SAME condition as the send above (2026-08-25): gating this retry on
         `studioUsed` meant a large FORM-ONLY creative — now a real payload — would be
         refused by /sfmc-submit and never re-filed, leaving Creative with an empty job */
      if (ok && hosted.html && !stored && recId) {
        try {
          const rev = await mirrorRevise({
            recordId: recId,
            html: hosted.html,
            project: form.projectName || naming.name,
            div: form.divisionCode || undefined,
          });
          if (rev && rev.ok && rev.assetId) { stored = true; filedAsset = String(rev.assetId); }
          else console.warn("creative attach fallback failed:", rev && rev.error);
        } catch (e) { console.warn("creative attach fallback failed:", e); }
      }
    } catch (e) {
      ok = false;
      /* a GatewayError carries the status the gateway answered with — the line Farhan
         greps the access log for; anything else is the thrown message as-is */
      const ge = e as { status?: number; message?: string };
      failMsg = (typeof ge?.status === "number" && ge.status > 0 ? ge.status + " · " : "") + String(ge?.message || e);
      console.warn("submit error:", e);
    }
    await delay(500);
    /* three states, not two: filed everywhere · filed but not everywhere · failed. The middle
       one used to render as the first, which is how a week of half-landed submits looked fine
       from this screen (2026-08-25). "Partly filed" is deliberately not reassuring — someone
       has to look. */
    if (!ok) console.warn("submit failed — nothing filed:", failMsg);
    setSubmitMsg(ok ? "" : failMsg);
    setDoneRec(recId);
    setDoneProblems(problemWheres);
    setDoneFiled(!ok ? "error" : delivered ? "filed" : "partial");
    await delay(400);
    setDoneStored(stored);
    setSubmitState(ok ? "done" : "error");
    /* a filed request has no draft to come back to; a FAILED one keeps its draft —
       that is exactly when someone needs to pick it back up (2026-09-08) */
    if (ok) onSubmitted?.({ assetId: filedAsset, html: filedHtml });
    } finally {
      sending.current = false;
    }
  }

  /**
   * START ANOTHER REQUEST — AND ACTUALLY START ONE (2026-08-28).
   *
   * The monolith's startOver re-enabled submit, swapped views and went back to
   * page 1; EVERY field value survived. TPU filed that twice on 2026-08-27 —
   * once as UX ("I have to refresh… in order to remove the previous template")
   * and once as a bug (a new division inheriting the previous division's project
   * name and rendered email). Both are this function.
   *
   * The refresh in TPU's report is the diagnosis: nothing here is persisted, so
   * only page memory could have been holding the stale copy.
   *
   * NOTHING VISIBLE CHANGES — same button, same label, same layout (per Jack).
   * Division and requested-by survive; the document and every other field don't.
   */
  function startOver() {
    setSubmitState("idle");
    setFullOpen(false);
    onResetDocument?.();          /* blocks, template, duplicate, undo history */
    onResetForm?.();              /* fields, keeping division + requested-by */
    /* FormView-local state App cannot see */
    setDupHtml(""); setFillHtml("");
    suppressAutoFill.current = 0;
    subjTouched.current = false; preTouched.current = false;
    subjAuto.current = ""; preAuto.current = "";
    lastDupJob.current = null;
    commFromPrice.current = 0;
    /* ITEM 7, AND THE AUTO-NAMER WAS NEVER THE BUG. `pnSuggest`'s effect already
       regenerates when the current name is the one IT last generated
       (`cur === pnAuto.current`) and correctly protects a name someone typed. What
       stranded TPU's old division name is that a DUPLICATE writes an explicit
       `project (copy)` (applyJob), which the namer then treats as user-authored and
       never touches again. Clearing both the value and the stamp hands the field
       back to the namer for the next division. */
    pnAuto.current = "";
    setMissing(new Set());
    setTouchedSteps(new Set());
    setNoteMsg("");
    setDoneName("");
    /* depDate/depTime follow `form.targetDeploy` (see the sync effect above) —
       clearing them here as well is what made Submit a silent no-op. */
    goStep(1);
  }

  /* ── THE STATUSES ARE LIVE (2026-08-27) ──────────────────────────────────────
     Creative works out of the Projects (Mirror) row, and its `status` field is the
     real pipeline stage — the same value the Review Board reads and writes. While
     the Done screen is open we poll `/mirror-jobs` and follow it, so a division
     that leaves the tab up watches the job move from Filed to With Creative to
     "your proof is ready" instead of staring at a frozen receipt.
     Bounded on purpose: every 20s, for 10 minutes. A failure is silent — the
     screen falls back to the submit's own verdict, which is still true; an error
     banner about our polling is not a division's problem. */
  useEffect(() => {
    if (submitState !== "done" || !doneRec) return;
    let stop = false, tries = 0;
    const tick = () => {
      if (stop || tries++ > 30) return;
      getMirrorJobs()
        .then((d) => {
          if (stop) return;
          const rows = (d && d.jobs) || [];
          const row = rows.find((r) => String(r["id"] || "") === doneRec)
            || (doneName ? rows.find((r) => String(r["emailName"] || "") === doneName) : undefined);
          const st = row ? String(row["status"] || "") : "";
          if (st) setDoneStage(st);
        })
        .catch(() => { /* the submit's verdict stands */ });
    };
    tick();
    const iv = window.setInterval(tick, 20000);
    return () => { stop = true; window.clearInterval(iv); };
  }, [submitState, doneRec, doneName]);

  /* ── THE REVIEW PANE IS THE DOCUMENT'S OWN HEIGHT ────────────────────────────
     `onLoad` alone is not enough: a `srcDoc` iframe can finish loading before
     React attaches the handler, and then the pane keeps its CSS fallback height
     and crops the footer off every send — the exact defect Jack reported on
     2026-08-27 ("need to show the full thing"). So measure on load AND poll for
     two seconds after the document changes, stopping at the first real answer. */
  useEffect(() => {
    if (!doneHtml) return;
    let tries = 0;
    const measure = () => {
      const f = donePrevRef.current;
      const d = f && f.contentDocument;
      const h = d ? Math.max(d.body ? d.body.scrollHeight : 0, d.documentElement ? d.documentElement.scrollHeight : 0) : 0;
      if (h > 40) { setDonePrevH(h); return true; }
      return false;
    };
    if (measure()) return;
    const iv = window.setInterval(() => { if (measure() || tries++ > 20) window.clearInterval(iv); }, 100);
    return () => window.clearInterval(iv);
  }, [doneHtml]);

  /* Escape closes the full-size read-through (same contract as the Studio's modals) */
  useEffect(() => {
    if (!fullOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFullOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullOpen]);

  /* yyyy-mm-dd → "Sep 25, 2026". Parsed as parts, never `new Date("2026-09-25")`,
     which is UTC-midnight and reads as the 24th west of Greenwich. */
  const depNice = (() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(depDate || ""));
    if (!m) return "";
    const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return MON[+m[2] - 1] + " " + +m[3] + ", " + m[1];
  })();

  /* The four step cards (`doneSteps` → Filed / Creative has your build / Proof / Scheduled)
     were REMOVED from this screen 2026-09-10 per division feedback relayed by Jack. The
     rule stays in services/doneStages.ts, tested, in case a status surface wants it again;
     nothing on this page reads it now. */

  /* The DONE screen's Airtable row mock lived here (ported from the monolith's
     renderAirtableMock, L5127) and was DELETED 2026-08-22 with Jack's option-D
     rebuild. It rendered our internal record — Airtable columns, a "Coming Soon"
     status pill, a "this is how your submission lands as a row" caption — as the
     largest element on the page. A division has no Airtable, could act on none of
     it, and "Coming Soon" read as though the request had not been filed. The done
     screen now shows their own rendered email instead (see `.dn2` above). */

  /* ── option lists for the combos ── */
  const divOpts = divisionOptions().map((d) => ({ v: d.code, label: d.label }));
  const initOpts = initiatives.map((i) => ({ v: i }));
  const focusOpts = REQ_FOCUS.map((o) => ({ v: o.v, label: o.label || o.v }));
  /* typeOpts retired with the Request Type field (2026-08-08) */
  /* the monolith message select is FLAT — no optgroups; option text is the
     internal legend "CODE = Label (offer)", value = the code (index.html L4201) */
  const msgOpts = MESSAGES.map((m) => ({ v: m[0], label: messageText(m[0]) }));
  const specOpts = SPECIALTY.map((s) => ({ v: s }));
  /* monolith fillMarketAdd (index.html L4208) lists EVERY division market —
     already-chipped ones included — behind a "+ Add market" placeholder option
     that renders as the highlighted first row (selectedIndex 0 → .on) */
  const marketAddOpts = marketOpts.map((m) => ({ v: m.code, label: `${m.code} — ${m.name}` }));
  /* "+ Add community or masterplan" — gold optgroup headers, already-picked filtered out (monolith fillCmAdd) */
  const cmAddGroups: ComboGroup[] = [
    { label: "Masterplans", opts: refMps.filter((v) => !mps.includes(v)).map((v) => ({ v: "MP|" + v, label: v })) },
    { label: "Communities", opts: refComms.filter((v) => !comms.includes(v)).map((v) => ({ v: "CM|" + v, label: v })) },
  ].filter((g) => g.opts.length);
  const aoiOpts = (refAois.length ? refAois : AOIS).map((a) => ({ v: a }));

  /* a USER template pick clears the dup preview and re-enables auto-fill
     (the monolith's setTemplate wrapper, L5429) */
  const pickTemplate = (n: string) => {
    /* A DIFFERENT TEMPLATE IS A DIFFERENT DOCUMENT (2026-08-28, KAN ×2).
       Clearing `dupHtml` was never enough: `tplBespoke` and `dupLoaded` survived,
       and `echTplName` is `tplBespoke ? "" : tplName` — so StudioView's assembly
       effect returned early and the newly-picked template was never built, however
       many times it was picked. The rail then fell through to `liveDocHtml`, which
       renders the OLD blocks, which is why the same event produced two reports (the
       Studio showing the duplicate, and the preview not loading the new template).

       Guarded on an actual change so re-clicking the current card is a no-op and
       cannot discard a build. Silent, per Jack — no confirm prompt. */
    const changed = n !== (form.template || "");
    F({ template: n });
    if (changed) onResetDocument?.();
    setDupHtml("");
    setFillHtml(""); /* the new template gets the same community content re-applied */
    suppressAutoFill.current = 0;
    userZoom.current = null; /* monolith setTemplate: zoom=null → back to auto-fit */
    setTplOpen(false);
  };

  return (
    <div className="rb">
      <div className="stage-wrap">
        {/* ── DONE section (monolith #done): replaces the WHOLE s1grid — form
            AND preview rail — the moment a valid submit fires ── */}
        {/* ── A FAILED SUBMIT GETS ITS OWN SCREEN (2026-09-22, Alexis Rashid, ATL) ─────────
            Since #19 (2026-08-19) the section below rendered for EVERY non-idle state — busy,
            done and error alike — so a submit the gateway refused, a network failure, and a
            filed request all showed the same green "Request submitted". The requester had no
            way to know, and "Submit another" then discarded the draft nothing had been filed
            from. This screen is the error state: no check mark, the reason in one line (the
            gateway's status and message, which is what the access log is searched by), the
            work kept, and two ways forward. `startOver` is deliberately NOT offered here. */}
        {submitState === "error" && (
          <section>
            <div className="done-wrap dn3-wrap">
              <div className="dn3" data-testid="submit-failed">
              <div className="done-check done-check--fail" aria-hidden="true">!</div>
              <h2>Request not submitted</h2>
              <p>Nothing was filed. Your work is still here.</p>
              {submitMsg && <p className="dn3-reason" data-testid="submit-reason">{submitMsg}</p>}
              <div className="navbar" style={{ justifyContent: "center", gap: 8 }}>
                <button type="button" className="btn ghost" style={{ marginTop: 20 }} onClick={() => setSubmitState("idle")}>Back to the request</button>
                <button type="button" className="btn gold" style={{ marginTop: 20 }} onClick={submit}>Try again →</button>
              </div>
              </div>
            </div>
          </section>
        )}
        {(submitState === "busy" || submitState === "done") && (
          <section>
            <div className="done-wrap dn3-wrap">
              <div className="dn3">
              {/* the check and the verdict wait for the answer — while the POST is in flight the
                  heading says so (2026-09-22); the layout underneath is the same either way */}
              <div className="done-check" style={submitState === "busy" ? { visibility: "hidden" } : undefined}>✓</div>
              <h2 data-testid="submit-heading">{submitState === "busy" ? "Submitting…" : "Request submitted"}</h2>
              {/* ── ONE SENTENCE, THEIRS (2026-09-10, division feedback via Jack): the copy names
                  who has the request and who to call — the Digital Ops Lead, not us. The old
                  built-it / template split is gone with the step rail below. */}
              <p>Your request has been submitted to Creative Studio - please reach out to your RMT's Digital Ops Lead if you have questions/changes to this request.</p>
              {/* ── SHOW THEM THEIR EMAIL (Jack picked option D, 2026-08-22) ──────
                  What was here: two status cards naming Airtable and "Stensul /
                  SFMC hand-off", then a MOCK AIRTABLE TABLE — the largest thing on
                  the page — captioned "this is how your submission lands as a row",
                  showing a Coming Soon status. A division has no Airtable, cannot
                  act on any of it, and "Coming Soon" reads as though their email
                  had NOT been filed. It showed them our plumbing instead of their
                  work.

                  Now: their own rendered email beside the three facts they'd need
                  if they had to chase it. Their build IS the confirmation, so a
                  wrong template or a missing photo is caught here rather than at
                  proof. Tool names are gone — "With Creative" is who has it.

                  NO PROMISED DATE (Jack: "do not say Proof by Tue Aug 26"). "You'll
                  get a proof before it sends" is true of every request; a specific
                  day is a commitment Creative's queue can't guarantee, and a missed
                  one costs more trust than the date ever bought. */}
              <div className="dn3-band">
                {/* ── LEFT: the creative that actually left, whole ─────────────── */}
                {doneHtml ? (
                  <div className="dn3-crt">
                    <div className="dn3-crthd">
                      <div className="dn3-cap">Your email</div>
                      <button type="button" className="dn3-link" onClick={() => setFullOpen(true)}>Open full size ↗</button>
                    </div>
                    <div className="dn3-win">
                      <iframe
                        ref={donePrevRef}
                        className="dn3-prev"
                        title="Your email"
                        srcDoc={doneHtml}
                        scrolling="no"
                        tabIndex={-1}
                        style={donePrevH ? { height: donePrevH } : undefined}
                        onLoad={(e) => {
                          /* the document's own height — anything assumed crops the footer */
                          const d = (e.currentTarget as HTMLIFrameElement).contentDocument;
                          const h = d ? Math.max(d.body ? d.body.scrollHeight : 0, d.documentElement ? d.documentElement.scrollHeight : 0) : 0;
                          if (h > 40) setDonePrevH(h);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="dn3-plate">
                    <div className="dn3-cap">Creative will build from</div>
                    <h3>{tplName}</h3>
                    <p>Nothing was built in the Studio, so Creative starts from the template and sends you a proof to review.</p>
                  </div>
                )}

                {/* ── RIGHT: the facts, and only the facts (2026-09-10) ──────────────────
                    The four-step rail (Filed / Creative has your build / Proof comes to you /
                    Scheduled) is gone — Jack, relaying division feedback: "just needs to be
                    removed all together. You might say creative has your build, but it
                    errored out on intake." A promise the intake cannot keep is worse than no
                    rail. The email name (a machine string) gave way to the PROJECT NAME, the
                    thing the division typed and Creative reads back to them. */}
                <div className="dn3-side dn3-side--facts">
                  <div className="dn3-facts">
                    <div className="dn3-fc"><div className="dn3-cap">Project name</div><div className="dn3-fv">{form.projectName || doneName || "—"}</div></div>
                    <div className="dn3-fc"><div className="dn3-cap">Division</div><div className="dn3-fv">{form.divisionCode || "—"}</div></div>
                    <div className="dn3-fc"><div className="dn3-cap">Scope</div><div className="dn3-fv">{comms[0] || mps[0] || markets[0] || "Market-level"}</div></div>
                    <div className="dn3-fc"><div className="dn3-cap">Deploys</div><div className="dn3-fv">{depNice || "Not set"}</div></div>
                  </div>
                </div>
              </div>
              <div className="navbar" style={{ justifyContent: "center", gap: 8 }}>
                {/* ── VIEW REQUESTS → THE DIVISION'S OWN QUEUE (2026-09-10) ──────────────────
                    Ashley Max (AZH): "can we change the 'view requests' at the end to the
                    airtable queue for now?" Jack: the AIM Division Interface, filtered per
                    division. The record-id deep link (2026-08-31) and the sandbox-queue
                    fallback (2026-09-09) both pointed into a base divisions cannot see; this
                    opens Creative Studio | AIM Division Interface › Active Projects with
                    Division Code preset from /refdata's `divisionId`. Always rendered — with no
                    id it opens the same page unfiltered, never a dead end. */}
                <a
                  className="btn ghost"
                  style={{ marginTop: 20, textDecoration: "none" }}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="done-queue"
                  href={divisionQueueUrl(refDivId)}
                >View requests {"\u2197"}</a>
                <button className="btn gold" style={{ marginTop: 20 }} onClick={startOver}>Submit another →</button>
              </div>
              </div>
            </div>
            {/* the same document at its authored 600px, for a real read-through */}
            {fullOpen && doneHtml && (
              <div className="dn3-ov" onClick={(e) => { if (e.target === e.currentTarget) setFullOpen(false); }}>
                <div className="dn3-ovin">
                  <div className="dn3-ovbar">
                    <div className="dn3-ovnm">{doneName}</div>
                    <button type="button" onClick={() => setFullOpen(false)}>Close</button>
                  </div>
                  <iframe
                    className="dn3-ovprev"
                    title="Your email, full size"
                    srcDoc={doneHtml}
                    scrolling="no"
                    style={donePrevH ? { height: donePrevH } : undefined}
                  />
                </div>
              </div>
            )}
          </section>
        )}
        <div className="s1grid" style={submitState !== "idle" ? { display: "none" } : undefined}>
          {/* ── left: form card ── */}
          <div ref={colRef}>
            <div className="formcard" ref={cardRef}>
              <>
              {/* stepper — .incomplete on touched steps with empty requireds (monolith refreshSteps) */}
              <div className="stepper">
                {(dsOn ? STEPS : STEPS_NO_DS).map((s, i) => {
                  const n = i + 1;
                  const inc = touchedSteps.has(n) && n !== step && !pageComplete(n);
                  const cls = "stp" + (n === step ? " active" : n < step ? " done" : "") + (inc ? " incomplete" : "");
                  return (
                    <div key={s.t} style={{ display: "contents" }}>
                      {i > 0 && <div className={"stp-line" + (n <= step ? " fill" : "")} />}
                      <div className={cls} onClick={() => goStep(n)}>
                        <div className="circ">{n}</div>
                        <div className="lab"><b>{s.t}</b><span>{s.s}</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="fc-rule" />

              <div className="fc-body" ref={bodyRef}>
                {/* the content-fit wrapper — fitAll CSS-zooms it so every step
                    fits its pane with zero scroll (viewport-lock engine) */}
                <div className="fc-zoom" ref={zoomRef}>
                {/* ══ STEP 1 · Creative Request ══ */}
                {step === 1 && (
                  <>
                    <div className="grid2">
                      <div className={"f" + fmiss("divisionCode")}>
                        <label>Division Code <span className="req">*</span></label>
                        <Combo value={form.divisionCode} onChange={onDivisionChange} options={divOpts} placeholder="— select —" />
                      </div>
                      <div className={"f" + fmiss("targetDueDate")}>
                        <label>Target Due Date <span className="req">*</span></label>
                        {/* ── NO PAST DATES (2026-08-27, Layne: "3 records of feedback around
                            being able to select dates that have passed") ────────────────────
                            `min` stops the PICKER at today; the submit gate below is what
                            actually enforces it, because a date can still be typed straight
                            into the field in every browser. */}
                        <input type="date" min={todayISO()} value={form.targetDueDate || ""}
                          onChange={(e) => F({ targetDueDate: e.target.value })} />
                        {isPastDate(form.targetDueDate) && <span className="datepast">{PAST_DATE_MSG.targetDueDate}</span>}
                      </div>
                    </div>


                    <div className={"f" + fmiss("initiative")}>
                      <label>Initiative <span className="req">*</span></label>
                      <Combo value={form.initiative || ""} onChange={(v) => F({ initiative: v })} options={initOpts}
                        placeholder={form.divisionCode ? "— select —" : "Select a division first"}
                        loading={initLoading} loadingText="Loading initiatives…" />
                      {initStale ? <span style={{ fontSize: 11, lineHeight: 1.35, color: "#8a4b00" }}>{initStale}</span> : null}
                    </div>

                    <div className={"f" + fmiss("reqFocus")}>
                      <label>Email Request Focus <span className="req">*</span> <Info tip={TIP.reqFocus} /></label>
                      <Combo value={reqFocus} onChange={(v) => F({ reqFocus: v })} options={focusOpts} />
                    </div>

                    {reqFocus === "Area of Interest" && (
                      <div className="f">
                        <label>Area of Interest</label>
                        {/* /refdata `areas` picklist; static AOIS until it answers. Plain enhanced
                            select (≤12 options → no search box, no free text), blank row clears. */}
                        <Combo value={form.areaOfInterest || ""} onChange={(v) => F({ areaOfInterest: v })}
                          options={aoiOpts} placeholder="— select —" clearable
                          loading={refLoading} loadingText="Loading areas…" />
                      </div>
                    )}

                    {reqFocus === "Community" && (
                      <div className="f">
                        <label>Communities / Masterplans <Info tip={TIP.cm} /></label>
                        <div className="chipbox">
                          <span className="chips">
                            {cmSel.map((it) => {
                              const e = it.kind === "MP" ? mpCols[it.name] : undefined;
                              const total = e && e.cols ? e.cols.length : 0;
                              const on = total - ((e && e.exc) || []).length;
                              return (
                                <span key={it.kind + "|" + it.name} className="tag">
                                  {it.name}
                                  {it.kind === "MP" && total > 1 && (
                                    <i style={{ fontStyle: "normal", opacity: .7, fontSize: 11 }}>{on}/{total}</i>
                                  )}
                                  {it.kind === "MP" && (
                                    <button type="button" title="Choose collections"
                                      style={{ border: 0, background: "transparent", cursor: "pointer", color: "inherit", fontSize: 11, padding: "0 1px" }}
                                      onClick={() => cmColsOpen(it.name)}>▾</button>
                                  )}
                                  <button onClick={() => removeCm(it)}>×</button>
                                </span>
                              );
                            })}
                          </span>
                          {(cmAddGroups.length > 0 || refLoading) && (
                            <Combo chip value="" onChange={addCm} options={cmAddGroups} placeholder="+ Add community or masterplan"
                              loading={refLoading} loadingText="Loading communities…" />
                          )}
                        </div>
                        {/* per-MP collections panel (monolith #cmCols / cmColsOpen, L4282–4309) */}
                        {colsOpenFor != null && (() => {
                          const e = mpCols[colsOpenFor];
                          if (!e || e.cols == null) return (
                            <div style={{ border: "1px solid #d9d4c8", borderRadius: 9, background: "#fbfcfe", padding: "10px 12px", marginTop: 7, fontSize: 12, color: "#475569" }}>
                              Finding {colsOpenFor} collections in SFMC…
                            </div>
                          );
                          if (!e.cols.length) return (
                            <div style={{ border: "1px solid #d9d4c8", borderRadius: 9, background: "#fbfcfe", padding: "10px 12px", marginTop: 7, fontSize: 12, color: "#94a3b8" }}>
                              No SFMC collections found for {colsOpenFor} — the masterplan will be used as one audience.
                            </div>
                          );
                          return (
                            <div style={{ border: "1px solid #d9d4c8", borderRadius: 9, background: "#fbfcfe", padding: "10px 12px", marginTop: 7 }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: "#1B365D", letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 7 }}>
                                {colsOpenFor} — collections in this send
                              </div>
                              {e.cols.map((c) => {
                                const off = e.exc.indexOf(c.name) > -1;
                                return (
                                  <label key={c.name} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 8, padding: "5px 0", fontSize: 12.5, color: off ? "#94a3b8" : "#334155", cursor: "pointer", textAlign: "left" }}>
                                    <input type="checkbox" checked={!off} style={{ accentColor: "#1B365D", width: 15, height: 15, flex: "0 0 auto", margin: 0 }}
                                      onChange={(ev) => cmColToggle(colsOpenFor, c.name, ev.target.checked)} />
                                    {c.name}{c.code ? <span style={{ color: "#b3bdcc", fontSize: 10.5 }}> {c.code}</span> : null}
                                  </label>
                                );
                              })}
                              <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 6 }}>Unchecked collections are left out of the audience and the email content.</div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    <div className={"f" + fmiss("market")}>
                      <label>Market <span className="req">*</span> <Info tip={TIP.market} /></label>
                      <div className="chipbox">
                        <span className="chips">
                          {markets.map((m) => (
                            <span key={m} className="tag">{m}<button onClick={() => setMarkets(markets.filter((x) => x !== m))}>×</button></span>
                          ))}
                        </span>
                        {/* clearable → the "+ Add market" placeholder renders as the
                            highlighted first row, like the monolith's option[0] */}
                        {/* ONE MARKET PER REQUEST (Jack, 2026-09-10: "for the market level we
                            should only allow one market code to be selected") — a pick
                            REPLACES the chip instead of adding a second one; `form.market`
                            stays an array so the payload shape is unchanged */}
                        <Combo chip clearable value="" onChange={(v) => { if (v) setMarkets([v]); }}
                          options={marketAddOpts} placeholder={markets.length ? "Change market" : "+ Add market"}
                          loading={refLoading} loadingText="Loading markets…" />
                      </div>
                    </div>

                    <div className={"f" + fmiss("projectName")}>
                      <label>Project Name <span className="req">*</span> <Info tip={TIP.projectName} /></label>
                      <input value={form.projectName} placeholder="e.g. Atlanta National Sales Event Eblast for 10/6" onChange={(e) => F({ projectName: e.target.value })} />
                    </div>

                    {/* ── STEP 1 ORDER — RESTORED (2026-09-15, Jack: "why did we change this …
                        put this back in the right order"). Division + Due Date → Initiative →
                        Email Request Focus → Area / Communities / Market → Project Name →
                        Email Nurture Template last, the order the form had before 09-12. The
                        09-12 reorder came from the 08-31 pilot-feedback row; the label rename
                        ("Email Request Focus") from the same row stays. */}
                    {/* Email Template Request Type removed per Jack 2026-08-08 — requestType
                        stays in state (default) so the template gate + submit mapping hold */}
                    {showTpl && (
                      <div className={"f" + fmiss("template")}>
                        <label>Email Nurture Template <span className="req">*</span></label>
                        <button className="combo-btn" type="button" onClick={() => setTplOpen(true)}>
                          <span className="combo-lbl">{tplName}</span><span className="combo-car">▾</span>
                        </button>
                      </div>
                    )}

                  </>
                )}

                {/* auto-fill status banner (monolith #autoFillNote, L867 — sits after the
                    step-1 page in fc-body, so it stays visible on every step) */}
                {afNote && (
                  <div className={"autofill-note" + (afNote.tone === "warn" ? " warn" : "")}
                    dangerouslySetInnerHTML={{ __html: afNote.msg }} />
                )}

                {/* ══ STEP 2 · Customize Eblast ══ */}
                {step === 2 && (
                  <>
                    {/* INTENTIONAL DIVERGENCE from the monolith (Katy Stenerson, 2026-08-19:
                        "Could we move Target Deployment date & time to be on top?"): the
                        deploy date/time is now the FIRST field on step 2, above the
                        subject/preheader card. Same state (depDate/depTime → writeDeploy),
                        same fmiss("deployDate") flag, same reqPairs(2) entry — only the
                        DOM order changed, so the section-jump still scrolls to it. */}
                    <div className="grid2 p2">
                      <div className={"f" + fmiss("deployDate")}>
                        <label>Target Deployment Date/Time <span className="req">*</span> <Info tip={TIP.deploy} /></label>
                        <div className="grid2 dt">
                          <input type="date" min={todayISO()} value={depDate} onChange={(e) => {
                            setDepDate(e.target.value); writeDeploy(e.target.value, depTime);
                            if (missing.has("deployDate")) { const m = new Set(missing); m.delete("deployDate"); setMissing(m); }
                          }} />
                          {/* 15-MINUTE STEPS (2026-09-12, CHB feedback on the pilot: "do we need a
                              long scroll of all 59 minutes?"). Chrome's picker lists one row per
                              `step`, so :00/:15/:30/:45 is what drops down; a typed minute still works. */}
                          <input type="time" step={900} value={depTime} onChange={(e) => { setDepTime(e.target.value); writeDeploy(depDate, e.target.value); }} />
                        </div>
                        {/* the TIME is part of this field, so today at 08:00 asked for at
                            12:40 is the same defect as a past date (2026-08-27) */}
                        {isPastDateTime(depDate, depTime) && <span className="datepast">{PAST_DATE_MSG.deployDate}</span>}
                      </div>
                    </div>
                    <div className="aicard">
                      <div className="aicard-body">
                        <div className={"f" + fmiss("subject")}>
                          <label>Subject Line <span className="req">*</span></label>
                          {/* ── THE CAP IS ENFORCED, NOT COACHED (2026-08-24) ────────────────────
                              Jack: "need to enforce the character count but subject line can be
                              up to 80." The counter turned red at 50 and let a division keep
                              typing — the screenshot that prompted this reads "88 / 50 chars",
                              which is a request that cannot be built. `maxLength` stops the
                              typing at the real ceiling, so an over-length subject cannot be
                              submitted at all. 80, not 50: 50 is the COACHING target (where the
                              counter turns amber) and 80 is the hard limit. */}
                          <input value={form.subject || ""} maxLength={SUBJ_HARD} placeholder="Your subject line" onChange={(e) => { subjTouched.current = true; F({ subject: e.target.value.slice(0, SUBJ_HARD) }); }} />
                          {/* the counter says the count and nothing else (Jack 2026-08-24:
                              "remove — over the 50 recommended"). Amber past the coaching
                              length is the whole signal; the sentence was noise. */}
                          <div className={"charc" + ((form.subject || "").length > SUBJ_COACH ? " warn" : "")}>{(form.subject || "").length} / {SUBJ_HARD} chars</div>
                        </div>
                        <div className="aicard-div" />
                        <div className={"f" + fmiss("preheader")}>
                          <label>Pre-Header Text <span className="req">*</span></label>
                          <input value={form.preheader || ""} maxLength={PRE_HARD} placeholder="Preview text under the subject" onChange={(e) => { preTouched.current = true; F({ preheader: e.target.value.slice(0, PRE_HARD) }); }} />
                          <div className={"charc" + ((form.preheader || "").length >= PRE_HARD ? " warn" : "")}>{(form.preheader || "").length} / {PRE_HARD} chars</div>{/* amber only AT the cap: 100 is the guidance and the ceiling, so there is no coaching band to warn inside */}
                        </div>
                      </div>
                      {/* INTENTIONAL DIVERGENCE from the monolith (Jack 2026-08-09,
                          pre-push review: "remove the ai feature for now"): the ✦ Draft
                          with AI entry point is removed entirely. AiCopyModal stays on
                          disk, unrendered — same pattern as Seg3 after the MVP cutover. */}
                    </div>
                    {/* Primary Message moved OUT of step 2 → step 3 (Audience), Katy
                        Stenerson 2026-08-19: "move the Primary message to be in the
                        Audience page". It is the field the audience recommendation is
                        keyed off (autoSF reads form.primaryMessage), so it now sits with
                        the audience controls. reqPairs() moved with it — see reqPairs(3). */}
                    <div className="custpanel">
                      <h3 className="sechd"><span className="b">✦</span><span className="t">Customize this creative</span><span className="rule" /></h3>
                      {/* monolith #custDynNote (L897/L4608): shown when the template's raw
                          HTML is AMPscript-dynamic — SFMC fills each community at send */}
                      { /%%[[=]/.test(previewHtml) && (
                        <p className="reqnote" style={{ display: "block", margin: "-4px 0 12px" }}>
                          Dynamic Fast Track template — customize it in Design Email. At send, SFMC auto-populates each community's real data.
                        </p>
                      )}
                      <div className="es-cta">
                        <div className="es-cta-hd">Customize in Design Email</div>
                        <div className="es-cta-list">
                          <div className="es-cta-row"><span className="ck">✓</span>Edit subject, headline, and body copy</div>
                          <div className="es-cta-row"><span className="ck">✓</span>Swap the hero and module images</div>
                          <div className="es-cta-row"><span className="ck">✓</span>Drag, reorder, and add real Lennar modules</div>
                        </div>
                        {/* text verbatim (monolith L905) — no build-status suffix.
                            The studio opens ON THE SELECTED TEMPLATE: when no doc is
                            loaded yet (boot default — no explicit gallery pick), the
                            real template HTML already in the preview rail is loaded
                            first, so the studio never opens the webinar block mock
                            (Jack 2026-08-09: "this is off"). */}
                        <button type="button" className="es-cta-open" onClick={openStudio}>✦ Open Design Email →</button>
                      </div>
                    </div>

                    {/* ══ Specialty branding — moved here from step 4 (2026-08-28) ══ */}
                    <h3 className="sechd" style={{ marginTop: 28 }}><span className="b">✦</span><span className="t">Specialty Branding</span><span className="rule" /></h3>
                    <div className={"f" + fmiss("specialtyBranding")}>
                      <label>Does the Masterplan or Community have Specialty Branding? <span className="req">*</span> <Info tip={TIP.specialty} /></label>
                      <Combo value={form.specialtyBranding || SPECIALTY_DEFAULT} onChange={(v) => F({ specialtyBranding: v })} options={specOpts} />
                    </div>
                    {/* ── THE LOGO LIVES IN BOX (2026-08-28 standup) ────────────────────
                        Specialty-branded communities have their own logo and it is NOT in
                        Bynder — Layne's team keeps them in Box, so the picker cannot offer
                        one. MVP answer, per the call: the requester pastes the Box link and
                        it travels with the request (it lands in the reference-files column,
                        labelled). Only asked when the answer to the question above makes a
                        logo relevant — a field nobody needs is a field everybody skips. */}
                    {(form.specialtyBranding || SPECIALTY_DEFAULT) !== SPECIALTY_DEFAULT && (
                      <div className="f">
                        <label>Logo link (Box)</label>
                        <input
                          value={form.specialtyLogoUrl || ""}
                          placeholder="https://lennar.app.box.com/…"
                          onChange={(e) => F({ specialtyLogoUrl: e.target.value })}
                        />
                        {urlProblem(form.specialtyLogoUrl || "") && (
                          <span className="datepast">{urlProblem(form.specialtyLogoUrl || "")}</span>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* ══ STEP 3 · Audience ══ — Send Scope only (monolith html.mvpaud;
                    the Seg3 builder stays on disk but never renders — MVP-only
                    cutover 2026-08-09) */}
                {step === 3 && (
                  <div className={"ssv" + fmiss("senderType") + fmiss("utmTarget") + fmiss("emailAudienceType") + fmiss("sfListsInclude")}>
                    <SendScope form={form} setForm={setFormClearing} seg={seg}
                      topSlot={
                        /* Primary Message — moved here from step 2 (Katy 2026-08-19).
                           Still FormView's field: same state key, same msgOpts, same
                           fmiss flag, and reqPairs(3) now carries it so a missing
                           value bounces to THIS step. */
                        <div className={"f" + fmiss("primaryMessage")}>
                          <label>Primary Message <span className="req">*</span> <Info tip={TIP.message} /></label>
                          <Combo value={form.primaryMessage || ""} onChange={(v) => F({ primaryMessage: v })} options={msgOpts} placeholder="— select —" />
                        </div>
                      } />
                  </div>
                )}

                {/* ══ STEP 4 · Disclaimer & Details (prod: Branding & Details, see dsOn) ══ */}
                {step === 4 && (
                  <>
                    {/* Specialty Branding MOVED to step 2 (2026-08-28 standup): it is a
                        creative decision about the email, so it belongs with the rest of
                        Customize Eblast — and the Box logo link that came with it has to
                        sit beside the question that asks for it. */}
                    {dsOn && (<>
                    {/* ── DISCLAIMER (2026-10-02, milestone 2 enhancement; services/disclaimer.ts) ──────────
                        The Disclaimer Builder Request Form's own lists, pre-picked from the template,
                        Step 1 and the email. Template items are locked (they ARE the default
                        disclaimer); detected ones can be removed; anything missed can be added. The
                        result is written into the email's footer — see the rail. No offers (M3/4),
                        and "Will this be a Grand Opening?" is never asked: the template answers it. */}
                    <h3 className="sechd"><span className="b">✦</span><span className="t">Disclaimer</span><span className="rule" /></h3>
                    {(["rel", "spec"] as const).map((list) => {
                      const picked = dsPicked.filter((p) => DS_ITEM[p.key].list === list);
                      const addable = DISCLAIMER_ITEMS.filter((i) => i.list === list && !dsPicked.some((p) => p.key === i.key));
                      return (
                        <div className="f" key={list} data-testid={"ds-" + list}>
                          <label>{list === "rel" ? "Relevant in Most Ads" : "Special or Unique Mentions"} <Info tip="Please select everything mentioned in your creative. Checked for you from the template, Step 1 and your email; add anything that was missed." /></label>
                          <div className="chipbox">
                            <span className="chips">
                              {picked.map((p) => (
                                <span key={p.key} tabIndex={0} aria-label={DS_ITEM[p.key].name + ". " + p.why}
                                  className={"tag ds-tag " + (p.fromTemplate ? "ds-tpl" : p.why === "Added by you" ? "ds-mine" : "ds-auto")}
                                  data-src={p.fromTemplate ? "template" : p.why === "Added by you" ? "you" : "auto"} data-why={p.why}>
                                  {DS_ITEM[p.key].name}
                                  {p.fromTemplate ? <span className="ds-src">template</span> : p.why !== "Added by you" ? <span className="ds-src">auto</span> : null}
                                  {!p.fromTemplate && (
                                    <button type="button" aria-label={"Remove " + DS_ITEM[p.key].name} onClick={() => dsSet({
                                      removed: Array.from(new Set([...(dsAns.removed || []), p.key])),
                                      added: (dsAns.added || []).filter((k) => k !== p.key),
                                    })}>×</button>
                                  )}
                                </span>
                              ))}
                            </span>
                            {addable.length > 0 && (
                              <Combo chip value="" options={addable.map((i) => ({ v: i.key, label: i.name }))} placeholder="+ Add"
                                onChange={(k) => { if (k) dsSet({ added: Array.from(new Set([...(dsAns.added || []), k])), removed: (dsAns.removed || []).filter((x) => x !== k) }); }} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* the follow-ups, marks, VIP and the request # share one 3-across grid so Step 4
                        keeps its zoom (Jack, 2026-10-07: "text looks very small") */}
                    <div className="ds-follow" data-testid="ds-follow">
                        {dsAsk.sqft && (
                          <div className={"f" + fmiss("dsSqftType")} data-testid="ds-sqft">
                            <label>Acreage, Dimensions or Sq. Ft. <span className="req">*</span> <Info tip="What is mentioned in your creative? Asked because Square Footage is checked; pre-filled from what your email says." /></label>
                            <Combo value={dsSqftAnswer(dsDet, dsAns)} onChange={(v) => dsSet({ sqftType: v })} options={SQFT_TYPES.map((v) => ({ v }))} placeholder="— select —" />
                          </div>
                        )}
                        {dsAsk.eventFood && (
                          <div className={"f" + fmiss("dsEventFood")} data-testid="ds-food">
                            <label>Event Food <span className="req">*</span> <Info tip={dsDet.foodHint && !dsAns.eventFood ? "Pre-filled Yes: " + dsDet.foodHint + "." : "Will your event have food or beverages?"} /></label>
                            <Combo value={dsFoodAnswer(dsDet, dsAns)} onChange={(v) => dsSet({ eventFood: v })} options={[{ v: "Yes" }, { v: "No" }]} placeholder="— select —" />
                          </div>
                        )}
                        {dsAsk.accolades && (
                          <>
                            <div className={"f" + fmiss("dsAccoladeType")}>
                              <label>Accolade Type <span className="req">*</span></label>
                              <Combo value={dsAns.accoladeType || ""} onChange={(v) => dsSet({ accoladeType: v })} options={[{ v: "Awards" }, { v: "Rankings" }]} placeholder="— select —" />
                            </div>
                            <div className={"f" + fmiss("dsAccoladeOrg")}>
                              <label>Accolade Organization <span className="req">*</span> <Info tip="Organization Name of the Award/Ranking." /></label>
                              <input value={dsAns.accoladeOrg || ""} placeholder="Organization name" onChange={(e) => dsSet({ accoladeOrg: e.target.value })} />
                            </div>
                            <div className="f">
                              <label>Accolade Year(s)</label>
                              <input value={dsAns.accoladeYear || ""} onChange={(e) => dsSet({ accoladeYear: e.target.value })} />
                            </div>
                          </>
                        )}
                        {dsDet.marks.length > 0 && (
                          <div className="f">
                            <label>Marks/logos to include</label>
                            <div className="chipbox"><span className="chips">{dsDet.marks.map((m) => <span key={m.name} tabIndex={0} className="tag ds-tag ds-auto" data-why={m.why}>{m.name}<span className="ds-src">auto</span></span>)}</span></div>
                          </div>
                        )}
                        {dsDet.vip && (
                          <div className="f">
                            <label>VIP Program <Info tip="If creative references “Join the VIP List”." /></label>
                            <div className="chipbox"><span className="chips"><span tabIndex={0} className="tag ds-tag ds-auto" data-why={dsDet.vip.why}>{dsDet.vip.name}<span className="ds-src">auto</span></span></span></div>
                          </div>
                        )}
                        {dsReqField}
                    </div>
                    </>)}
                    <h3 className="sechd"><span className="b">✦</span><span className="t">Request Details</span><span className="rule" /></h3>
                    {reqTypeRule(reqType).ref && (
                      <div className="f">
                        <label>Existing Template/Reference Files <Info tip={TIP.refFiles} /></label>
                        {/* no placeholder — monolith parity (L938) */}
                        <textarea value={form.referenceFiles || ""} onChange={(e) => F({ referenceFiles: e.target.value })} />
                      </div>
                    )}
                    {/* production (no builder): the request # sits here, as before #364 */}
                    {!dsOn && dsReqField}
                    <div className="f">
                      <label>Anything Else <Info tip={TIP.anythingElse} /></label>
                      {/* no placeholder — monolith parity (L940); submit errors land on the
                          done view's warn pill, not here */}
                      <textarea value={form.anythingElse || ""} onChange={(e) => F({ anythingElse: e.target.value })} />
                    </div>
                  </>
                )}
                </div>
              </div>

              {/* nav footer — monolith order (L944–951): Dup · Back · page note · spacer · Next/Submit */}
              <div className="navbar">
                {/* hidden by default since 2026-09-10 (Jack: "hide/remove the duplicate feature
                    for now") — services/features.ts flips it back on, nothing behind it removed */}
                {step === 1 && dupJobEnabled() && (
                  <button type="button" className="dupfoot" title="Pull a previous eblast request and copy all its details into a new one"
                    onClick={() => setDupOpen(true)}>Duplicate A Past Job</button>
                )}
                {step > 1 && <button className="btn ghost" onClick={() => goStep(step - 1)}>← Back</button>}
                <span className={"pgnote" + (noteMsg ? " bad" : "")}>{noteMsg || `Page ${step} of 4`}</span>
                <span className="spacer" />
                {step < 4
                  ? <button className="btn" onClick={() => goStep(step + 1)}>Next</button>
                  : <button className="btn gold" disabled={submitState === "busy"} onClick={submit}>Submit request →</button>}
              </div>
              </>
            </div>
          </div>

          {/* ── right: live preview rail (monolith parity) ── */}
          <div>
            <div className="prev-rail">
              <div className="pv-top">
                <span className="pl">Preview</span>
                <button className="pvbtn" type="button" title="Browse the template gallery" onClick={() => setTplOpen(true)}>
                  <span className="pvt-name">{tplName}</span><span className="pvt-caret">▾</span>
                </button>
                <span className="zoomctl">
                  <button type="button" title="Zoom out" onClick={zOut}>−</button>
                  <span className="zpct">{Math.round(previewZoom * 100)}%</span>
                  <button type="button" title="Zoom in" onClick={zIn}>+</button>
                  <button type="button" title="Fit" onClick={zFit}>⤢</button>
                </span>
                <div className="pv-toggle"><button type="button" className="on" onClick={openStudio}>Design Email</button></div>
              </div>
              <div className="deviceframe">
                {/* LIVE-CONTENT FILL INDICATOR (2026-09-11; Uche on the testing call; mock
                    SPINNER-MOCK-20260911.html panel A, approved by Jack). Only while the
                    dynamic feed is in flight AND an email is showing — the template stays
                    visible underneath, dimmed, so nothing flashes to blank. This is the
                    same window that used to make the Done gate flash (#224). */}
                {showPill && (
                  <>
                    <div className="pv-bar" aria-hidden="true"><i /></div>
                    <div className="pv-fill" role="status" data-testid="pv-fill">
                      <span className="pv-pill"><span className="pv-spin" />{pillText}</span>
                    </div>
                  </>
                )}
                <div className="realwrap" ref={realwrapRef}>
                  {shownHtml ? (
                    /* .zoomsizer carries NO computed height — the ResizeObserver above sizes it
                       from the painted child. See that effect for why. */
                    <div className="zoomsizer" ref={zoomSizerRef}>
                      <div className="zoominner" ref={zoomInnerRef} style={{ transform: `scale(${previewZoom})` }}>
                        {/* #realFrame (monolith L37): white iframe surface, centered — the
                            #faf8f3 realwrap shows only AROUND the email, never behind it */}
                        <iframe title="Email preview" srcDoc={shownHtml} width={600} height={frameH} scrolling="no" onLoad={onFrameLoad} style={{ border: 0, display: "block", margin: "0 auto", maxWidth: "100%", background: "#fff" }} />
                      </div>
                    </div>
                  ) : (
                    <div className="prev-empty">Pick a template or build in Design Email to preview it here.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* template picker modal — the Templates tab is the LIVE SFMC template list
          and nothing else (2026-08-19). Every card renders the template assembled
          from Layne's own blocks; the ★ Favorites tab is unchanged. */}
      {tplOpen && (
        <MvpTemplateGallery
          names={echTpls.names} status={echTpls.status} onRetry={echTpls.retry} version={echTpls.version}
          current={tplName}
          div={form.divisionCode}
          onPick={pickTemplate}
          onPickFavorite={(name, html) => {
            /* monolith favorite pick (index.html L5072–5079): close the modal, show the
               saved HTML in the preview, load it into the Creative Studio silently.
               Reset first, same rationale as a duplicate (#74): whatever ECH document
               was on the canvas has nothing to do with the favorite, and without the
               reset `liveDoc` outranks it — the favorite was neither previewed nor
               submitted whenever the Studio had assembled anything (evaluator R3). */
            onResetDocument?.();
            suppressAutoFill.current = Date.now() + 20000;
            setDupHtml(html);
            onLoadStudioHtml?.("★ " + name, html);
            setTplOpen(false);
            notify("★ " + name + " — showing in the preview.");
          }}
          onPickDraft={onOpenNamedDraft ? (d) => { setTplOpen(false); onOpenNamedDraft(d); } : undefined}
          notify={notify}
          onClose={() => setTplOpen(false)} />
      )}

      {/* duplicate-a-past-job modal — MVP: only jobs built from the 5 templates */}
      {dupOpen && dupJobEnabled() && (
        <DupModal div={form.divisionCode} onApply={applyJob} onClose={() => setDupOpen(false)} notify={notify} />
      )}

      {/* bottom-center toast (monolith __fvToast) */}
      <div className={"rb-toast" + (toastOn ? " on" : "")}>{toastMsg}</div>
    </div>
  );
}
